import { createServer, type IncomingMessage, type ServerResponse } from "http"
import { createReadStream } from "fs"
import { writeFileSync, existsSync, unlinkSync, mkdirSync, readFileSync, watch, type FSWatcher } from "fs"
import { resolve, dirname, extname, normalize, sep } from "path"
import { loadDaemonConfig, validateWorkspaces, type DaemonConfig } from "./config"
import { AgentRegistry, setGlobalRegistry } from "@/agents/registry"
import { setAgentRegistry } from "@/agents/registry-instance"
import { resolvePermission } from "@/agents/runtime"
import { registerAllBuiltins, listBuiltins, runBuiltin, getBuiltin } from "@/actions/builtin"
import { MessageRouter } from "@/channels/router"
import { setMessageRouter } from "@/channels/router-instance"
import { TelegramAdapter } from "@/channels/telegram"
import { WhatsAppAdapter } from "@/channels/whatsapp"
import { GitLabAdapter } from "@/channels/gitlab"
import { GitHubAdapter } from "@/channels/github"
import { WebRtcSignalBroker, type WebRtcSignal } from "@/channels/webrtc-signal"
import { CALL_PAGE_HTML } from "./call-page"
import { BotManager } from "./bot-manager"
import { CronScheduler } from "@/crons/scheduler"
import { readCronRunHistory } from "@/crons/run-history"
import { ProjectRulesStore } from "@/projects/rules"
import { Logger } from "./logger"
import { EventBus, parseKindsParam } from "./event-bus"
import { WebhookHandler } from "./webhooks"
import { openDb, pruneSqliteTables, insertTaskQueue, completeTaskQueue, getTaskQueue, listTaskQueueByConversation } from "@/storage/sqlite"
import { newEventId } from "@/intent/ulid"
import { attachSqliteSubscribers } from "@/storage/subscribers"
import { attachProcedureWatcher } from "./procedure-watcher"
import { getUsageReadMode, loadTodayRollup } from "@/storage/usage-query"
import { getTrace, listTraces, cleanupOrphanedTraces } from "@/storage/traces"
import { ProcessRegistry } from "@/agents/process-registry"
import { ClaudeProcessFactory, readClaudeMdHashSafe } from "@/agents/claude-process-factory"
import { setProcessRegistry } from "@/agents/process-registry-instance"
import { loadPlugins, type LoadedPlugin } from "@/plugins"
import { getLedgerMode } from "@/intent/mode"
import { getDefaultLedger } from "@/intent/instance"
import { recordMeshDispatch } from "@/intent/sources/mesh"
import { setDefaultGovernance } from "@/intent/governance"
import { agentCanHandleIntent, withinDelegationBudget } from "@/agents/capabilities"
import { A2AMesh } from "@/a2a/mesh"
import { setMesh } from "@/a2a/mesh-instance"
import { decideMeshAuth, isLoopback } from "@/daemon/mesh-auth"
import { setTopbarFeatures } from "@/daemon/topbar"
import { resolveAgentCredential } from "@/integrations/resolve"
import { HookRegistry, loadHooks } from "@/hooks"
import {
  RunStore as WorkflowRunStore,
  WorkflowDispatcher,
  WorkflowStore,
  startWorkflowTriggers,
  workflowSchema,
  type AgentExecuteRequest,
  type AgentExecuteResponse,
  type MeshForwarder as WorkflowMeshForwarder,
} from "@/workflows"
import { createWorkflowHookHandlers } from "@/workflows/hooks"
import { getWorkflowDraft } from "@/workflows/absorb"
import { renderWorkflowYaml } from "@/workflows/yaml"
import { resolveAutoRunInputs } from "@/workflows/inputs"
import { LandscapeBuilder } from "@/agents/landscape"
import { AgentMemory } from "@/agents/agent-memory"
import { ContactDirectory } from "@/agents/contacts"
import { syncMcpToWorkspace, type McpServerMap } from "@/agents/agent-mcp"
import { bootstrapCodegraphIndexes, effectiveMcpConfig } from "@/agents/codegraph-bootstrap"
import { REMEMBER_SKILL_BODY, REMEMBER_SKILL_FILENAME } from "@/agents/skills/remember-skill"
import { HeartbeatManager } from "@/agents/heartbeat"
import { setupAllWorkspaces } from "@/agents/workspace-setup"
import { checkPayload, type PreToolUsePayload } from "@/guard"
import { getAttachRegistry, isDeliveryMode } from "@/attach"
import { onSessionStart, onPrompt, onStop, onSessionEnd, type HookPayload } from "@/attach/service"
import { ServiceMatcher } from "@/services/matcher"
import { BusinessLayer } from "@/business"
import { listAgentFiles } from "./file-ops"

// --- AgentX Daemon: the thin orchestration layer ---
//
// This is NOT an AI runtime. It's a message router + scheduler + mesh
// that triggers Claude Code sessions in workspace directories.
//
// Each agent = a workspace with .claude/ config
// agentx just orchestrates WHEN and WHERE Claude Code runs.

export class AgentXDaemon {
  private config: DaemonConfig
  private registry: AgentRegistry
  private router: MessageRouter
  private cron: CronScheduler
  /** Per-project webhook rules. Hot-reloaded from .agentx/projects/.
   *  Wired into the gitlab/github adapters at startup so they can gate
   *  events and resolve runbook paths. */
  private projectRules: ProjectRulesStore
  private mesh?: A2AMesh
  private hooks: HookRegistry
  private landscape: LandscapeBuilder
  private heartbeat: HeartbeatManager
  private business?: BusinessLayer
  private httpServer?: ReturnType<typeof createServer>
  private attachSweep?: ReturnType<typeof setInterval>
  private webhooks: WebhookHandler
  private github?: GitHubAdapter
  private webrtc?: WebRtcSignalBroker
  private botManager?: BotManager
  private workflowDispatcher?: WorkflowDispatcher
  private workflowStore?: WorkflowStore
  private workflowRuns?: WorkflowRunStore
  private db: import("better-sqlite3").Database | null = null
  private loadedPlugins: LoadedPlugin[] = []
  private readonly agentMemory: AgentMemory = new AgentMemory()
  private contacts!: ContactDirectory
  /** Persistent-claude process registry. Null when no agent has
   *  persistentProcess: true (legacy spawn-per-task path). */
  private processRegistry: ProcessRegistry | null = null
  /** Unified observability bus. Publishers (workflow engine, mesh,
   *  channels, signals) emit here; subscribers (SSE, CLI, board
   *  dashboard) filter by kind / workflow / actor. Created eagerly so
   *  early boot events don't fall through. */
  readonly events: EventBus = new EventBus()
  private log: (...args: unknown[]) => void
  private sseClients: Set<ServerResponse> = new Set()
  private configPath?: string
  private configWatcher?: FSWatcher
  private reloadTimer?: ReturnType<typeof setTimeout>

  constructor(configPath?: string) {
    const logger = new Logger("agentx")
    const baseLog = logger.asConsoleLog()

    // Wrap log to also broadcast to SSE clients
    this.log = (...args: unknown[]) => {
      baseLog(...args)
      const line = args.map(a => typeof a === "string" ? a : JSON.stringify(a)).join(" ")
      this.broadcastSSE("log", line)
    }

    // Load config
    this.log("Loading configuration...")
    this.configPath = configPath
    this.config = loadDaemonConfig(configPath)

    // Initialize the contact directory now that `this.log` is available.
    // Empty file (or missing file) is fine — operators populate it later.
    this.contacts = new ContactDirectory(process.cwd(), this.log)

    // The daemon renders a few shell pages itself (/inbox, /processes) —
    // give their topbar the same feature flags the 4202 dashboard uses.
    setTopbarFeatures({
      business: this.config.business?.enabled === true,
    })

    // Validate
    const warnings = validateWorkspaces(this.config)
    for (const w of warnings) {
      this.log(`  ⚠ ${w}`)
    }

    // Set up agent workspaces with Claude Code best practices (non-destructive)
    const [, portStr] = this.config.node.bind.split(":")
    setupAllWorkspaces(this.config.agents, portStr || "19900", this.log)

    // Initialize hooks
    this.hooks = new HookRegistry()
    loadHooks(process.cwd(), this.hooks)

    // Initialize landscape builder
    this.landscape = new LandscapeBuilder(this.config)

    // Initialize agent registry
    this.registry = new AgentRegistry(this.config, this.log)
    setGlobalRegistry(this.registry)
    setAgentRegistry(this.registry)
    this.registry.setLandscape(this.landscape)

    // Initialize heartbeat manager
    this.heartbeat = new HeartbeatManager(this.registry, this.log)
    for (const [id, agent] of Object.entries(this.config.agents)) {
      if (agent.heartbeat?.enabled) {
        this.heartbeat.register(id, agent.heartbeat)
      }
    }

    // Initialize message router
    this.router = new MessageRouter(this.registry, this.config, this.hooks, this.log)
    // Publish the singleton so tier-3 actions (channel.reply, etc.) and the
    // MCP tool layer can post outbound messages through the canonical send
    // path without re-implementing dedupe / marker / identity / ledger.
    setMessageRouter(this.router)

    // Initialize service matcher (automated client services)
    if (Object.keys(this.config.services).length > 0) {
      const serviceMatcher = new ServiceMatcher(this.config.services, this.log)
      this.router.setServiceMatcher(serviceMatcher)
    }

    // Per-project webhook rules — load from .agentx/projects/ before any
    // adapter starts so the very first webhook honours them. Watch the
    // directory for changes (debounced inside the store). Constructed
    // unconditionally — empty directory = empty store = legacy behaviour
    // for every project.
    this.projectRules = new ProjectRulesStore(
      resolve(process.cwd(), ".agentx/projects"),
      (...args) => this.log("[projects]", ...args),
    )
    {
      const result = this.projectRules.load()
      this.log(`Loaded ${result.count} project rule(s) from .agentx/projects/${result.errors > 0 ? ` (${result.errors} validation error(s))` : ""}`)
      this.projectRules.startWatching(() => {
        // No need to re-wire adapters: they hold a reference to the store
        // and lookups are dynamic on each event.
      })
    }

    // Initialize cron scheduler with failure notifications
    this.cron = new CronScheduler(this.config, this.registry, this.hooks, this.log)
    this.cron.setNotifyCallback(async (jobId, agent, error, consecutiveErrors) => {
      const msg = `Cron "${jobId}" failed (${consecutiveErrors}x)\nAgent: ${agent}\nError: ${error.slice(0, 300)}`
      this.log(`[CRON ALERT] ${msg}`)
      this.broadcastSSE("cron-failure", JSON.stringify({ jobId, agent, error, consecutiveErrors }))

      // Send to the cron job's configured notify destination (if set)
      const cronDef = this.config.crons[jobId]
      if (cronDef?.notify) {
        try {
          await this.router.sendOutbound({
            channel: cronDef.notify.channel,
            chatId: cronDef.notify.chatId,
            text: `🔴 **Cron "${jobId}" failed** (${consecutiveErrors}x)\n${error.slice(0, 300)}`,
            agentId: agent,
            accountId: cronDef.notify.accountId,
          })
        } catch (e: any) {
          this.log(`[CRON ALERT] notify send failed: ${e.message}`)
        }
      }
    })

    // Initialize mesh (if enabled)
    if (this.config.mesh.enabled) {
      this.mesh = new A2AMesh(this.config, this.log)
      this.router.setMesh(this.mesh)
      // Register on the singleton so built-in actions (mesh.delegate)
      // can call into the mesh without threading the instance through.
      setMesh(this.mesh)
      // Bridge mesh peer-state transitions into the event bus so
      // operators watching /events see recoveries + losses in real time.
      this.mesh.onPeerChange((e) => {
        this.events.publish({
          kind: "mesh",
          peer: e.peer,
          healthy: e.healthy,
          skills: e.skills,
          delta: e.delta,
        })
      })
      // Let the registry reach the mesh so it can forward unknown-agent
      // tasks to a peer that advertises the requested agent. Keeps
      // workflow authoring portable: a workflow that references
      // `coo-agent` works on any node in the mesh as long as SOMEONE
      // hosts that agent.
      this.registry.setMeshFallback({
        findPeerWithSkill: (skillId) => this.mesh!.findPeerWithSkill(skillId),
        sendTask: (peer, text, agentId) => this.mesh!.sendTask(peer, text, agentId),
        directory: () => this.mesh!.directory(),
      })
    }

    // Initialize webhook handler (after mesh so mesh-forwarding works)
    this.webhooks = new WebhookHandler(this.registry, {}, this.log, this.mesh, this.config.webhooks, this.hooks)

    // Move 2: open SQLite + attach bus subscribers. Best-effort — if the
    // native binding isn't available (operator hasn't run pnpm install),
    // openDb returns null and subscribers are skipped. Existing JSON
    // writes continue regardless. SQLite is observability-grade for now.
    try {
      const db = openDb()
      this.db = db
      if (db) {
        // Cancel any traces left in-flight from a previous daemon lifetime.
        // Without this, hard-killed tasks accumulate forever and clutter
        // `agentx trace list --status in-flight`. Mirrors the intent
        // ledger's startup cleanup (rescue plan a8514d9). Runs BEFORE
        // subscribers attach so the cleanup can't race with new starts.
        const cleaned = cleanupOrphanedTraces(db)
        if (cleaned > 0) {
          this.log(`  Traces: canceled ${cleaned} orphaned in-flight row(s) from prior run`)
        }
        attachSqliteSubscribers(db)
        this.log(`  SQLite: ${db.name}`)
        // Procedure miner's on-task trigger — counts recurring activity
        // patterns after each successful task (no-op unless
        // procedures.extraction.onTaskCompletion is enabled).
        attachProcedureWatcher(db, this.config, (m) => this.log(m))
      } else {
        this.log(`  SQLite: not opened (native binding unavailable or path unwritable)`)
      }
    } catch (e: any) {
      this.log(`  SQLite: skipped (${e.message})`)
    }

    // Persistent-process registry (improvement plan #5, persistent flavor).
    // Only spin up when at least one agent opts in via persistentProcess:
    // true; otherwise the singleton stays null and executeTask follows the
    // legacy spawn-per-task path. Caps + idle-eviction defaults from
    // ProcessRegistryConfig are operator-tunable later if needed.
    const persistentAgents = Object.values(this.config.agents).filter((a) => (a as any).persistentProcess)
    if (persistentAgents.length > 0) {
      try {
        const factory = new ClaudeProcessFactory({ log: (m) => this.log(`  ${m}`) })
        // Operator-configurable pool eviction (handoff #8). Defaults match
        // the registry's prior hardcoded behavior; agentx.json processPool.*
        // overrides them.
        const poolCfg = (this.config as any).processPool ?? {}
        const procReg = new ProcessRegistry({
          factory,
          log: (m) => this.log(`  ${m}`),
          idleTimeoutMs: (poolCfg.maxIdleSeconds ?? 30) * 1000,
          staleTimeoutMs: (poolCfg.maxAgeSeconds ?? 2700) * 1000,
          sweepIntervalMs: (poolCfg.sweepIntervalSeconds ?? 5) * 1000,
          // Drift detection: re-read each handle's workspace CLAUDE.md
          // hash on every sweep. When the hash changes (user edited
          // an agent's prompt, daemon's auto-refresh ran, etc.), the
          // sweeper kills the idle handle so the next dispatch picks
          // up the new file. Mid-turn handles get checked next idle.
          currentWorkspaceHash: (_key, workspace) => readClaudeMdHashSafe(workspace),
        })
        procReg.start()
        setProcessRegistry(procReg)
        this.processRegistry = procReg
        this.log(`  ProcessRegistry: enabled for ${persistentAgents.length} agent(s)`)
      } catch (e: any) {
        this.log(`  ProcessRegistry: skipped (${e.message})`)
      }
    }

    // Initialize business layer (if enabled)
    if (this.config.business?.enabled) {
      try {
        this.business = new BusinessLayer(
          this.config.business,
          this.config,
          this.registry,
          this.router,
          this.log,
        )
        this.router.setBusiness(this.business)

        // Phase 3 — wire org-chart governance into decideAndCommit when
        // INTENT_PM_GATE_ENABLED is set. Default off so the live shadow
        // soak isn't disturbed; flip on per-deployment after soak
        // findings settle. The DispatchGovernance hooks read from
        // BusinessLayer.org so PM/canHandle changes apply on next event
        // without a daemon restart.
        if (process.env.INTENT_PM_GATE_ENABLED === "true" || process.env.INTENT_PM_GATE_ENABLED === "1") {
          const org = this.business.org
          const agents = this.config.agents
          setDefaultGovernance({
            pmFor: (project) => org.pmFor(project),
            // canHandle layers two checks:
            //   1. Org-chart membership (Phase 3) — agent is registered.
            //   2. Phase 5 typed-capability check — agent's `intents`
            //      list (when non-empty) must include the dispatched
            //      intent. When the list is empty (default), handles all.
            canHandle: (agentId, project, intent) => {
              // Synthetic dispatch identifiers (workflow runs, mesh
              // forwards) aren't real agents — they're handles for an
              // already-decided dispatch. Capability and org-chart
              // checks were applied before the synthetic ID was minted
              // (or are simply N/A for the run handle), so the ledger's
              // canHandle veto here is structurally a no-op for them.
              // Phase 1 soak finding: gating on these produced 290+
              // false-positive divergences across both nodes.
              if (/^(workflow-run|mesh-fwd):/.test(agentId)) return true
              if (!org.canHandle(agentId, project, intent)) return false
              return agentCanHandleIntent(agents[agentId], intent)
            },
            // Phase 8 — delegation-depth check. Walks the ledger's
            // prior decisions on (project, subject) and refuses
            // dispatches past the target agent's maxDelegationDepth.
            withinDelegationBudget: (agentId, project, subject) =>
              withinDelegationBudget(getDefaultLedger(), agents[agentId], agentId, project, subject),
          })
          this.log("  Ledger governance: PM gate + capability + delegation-depth ENABLED")
        }
      } catch (e: any) {
        this.log(`[business] initialization failed: ${e.message}`)
      }
    }
  }

  async start(): Promise<void> {
    this.log("")
    this.log("  ┌─────────────────────────────────────┐")
    this.log("  │           agentx daemon              │")
    this.log("  └─────────────────────────────────────┘")
    this.log("")
    this.log(`  Node: ${this.config.node.name} (${this.config.node.id})`)
    this.log(`  Bind: ${this.config.node.bind}`)
    this.log("")

    // 0. Phase 1 — clean up orphaned in-flight ledger dispatches from
    //    the previous process. Their agents died with the previous
    //    daemon; without writing a "canceled" resolution they'd block
    //    Inv-ActiveTaskSafety on the same (project, subject) forever.
    //    Only fires when the ledger is reachable (some source has
    //    mode != "off"); the IntentLedger singleton is lazy and
    //    construction would otherwise be skipped entirely under
    //    deploy-default mode=off.
    try {
      const anySourceActive = (
        ["telegram", "slack", "whatsapp", "discord", "gitlab", "github", "workflow", "cron", "mesh"] as const
      ).some((s) => getLedgerMode(s) !== "off")
      if (anySourceActive) {
        const closed = getDefaultLedger().cleanupOrphanedDispatches("daemon-restart")
        if (closed > 0) {
          this.log(`  Ledger: cleaned up ${closed} orphaned in-flight dispatch(es) from previous process`)
        }
      }
    } catch (e: any) {
      this.log(`  Ledger orphan-cleanup failed: ${e?.message ?? e}`)
    }

    // 0.5. Move B — load JS/TS plugins. Each plugin's setup() runs here so
    //      it can register channel adapters BEFORE startChannels() and
    //      attach bus subscribers BEFORE any event fires. setup() is
    //      raced against a 15s timeout per plugin; failures log and
    //      continue. Plugin-registered channels are folded into the
    //      router right after startChannels() so they participate in
    //      routing alongside built-in adapters.
    try {
      const builtInChannelNames = new Set([
        "telegram", "whatsapp", "discord", "slack", "gitlab", "github",
      ])
      this.loadedPlugins = await loadPlugins({
        config: this.config,
        agents: new Map(Object.entries(this.config.agents)),
        log: this.log,
        isChannelNameTaken: (n) => builtInChannelNames.has(n),
        daemonVersion: process.env.npm_package_version,
      })
      if (this.loadedPlugins.length > 0) {
        this.log(`  Plugins: ${this.loadedPlugins.length} loaded — ${this.loadedPlugins.map(p => p.manifest.name).join(", ")}`)
      }
    } catch (e: any) {
      // loadPlugins() is supposed to never throw, but defend anyway —
      // a plugin failure must not abort daemon boot.
      this.log(`  Plugins: loader threw (${e?.message ?? e}) — continuing without plugins`)
      this.loadedPlugins = []
    }

    // 1. Start channels
    await this.startChannels()
    // After startChannels, fold plugin-registered channels into the router
    // and call their start(). Done here (not inside startChannels itself)
    // so the lifecycle ordering is explicit: built-in channels first,
    // plugin channels second.
    for (const p of this.loadedPlugins) {
      for (const ch of p.channels) {
        try {
          this.router.addChannel(ch)
          await ch.start()
          this.log(`  Plugin channel: ${ch.name} (from ${p.manifest.name})`)
        } catch (e: any) {
          this.log(`  Plugin channel "${ch.name}" failed to start: ${e?.message ?? e}`)
        }
      }
    }

    // 2. Start cron scheduler
    await this.cron.start()

    // 3. Start mesh
    if (this.mesh) {
      await this.mesh.start()
    }

    // 4. Build agent landscape (after mesh so remote peers are discovered)
    const meshPeers = this.mesh?.directory().map(p => ({
      peer: p.peer,
      healthy: p.healthy,
      skills: p.skills,
    })) || []
    this.landscape.build(meshPeers)
    this.log(`  Landscape: built for ${Object.keys(this.config.agents).length} agents`)

    // 5. Schedule midnight cost tracking hook + catch up any missed days.
    // Catch-up is idempotent and bounded to 30 days — a daemon restarted
    // after an outage backfills TOKEN_COSTS.md instead of silently losing
    // those rows. (The previous behavior only ever scheduled tomorrow's
    // midnight run; any restart before midnight lost that day's row.)
    this.scheduleMidnightHook()
    try {
      const yesterday = new Date(Date.now() - 86400_000).toISOString().slice(0, 10)
      const appended = this.registry.getTokenTracker().catchUpTokenCosts(yesterday)
      if (appended.length > 0) {
        this.log(`  Cost tracking: caught up ${appended.length} missed day${appended.length === 1 ? "" : "s"} (${appended[0]}${appended.length > 1 ? ` → ${appended[appended.length - 1]}` : ""})`)
      }
    } catch (err: any) {
      this.log(`  Cost tracking: catch-up failed (non-fatal): ${err.message}`)
    }

    // 5b. Start business layer day cycle (if configured)
    if (this.business) {
      this.business.start()
      this.log(`  ${this.business.summary()}`)
    }

    // 6. Start HTTP API
    await this.startHttpApi()

    // Print agent summary
    this.log("")
    this.log("  Agents:")
    for (const agent of this.registry.list()) {
      this.log(`    ${agent.id} (${agent.tier}) → ${agent.workspace}`)
    }

    // Install the `remember` skill + sync existing memory into each
    // agent's workspace. Write-if-absent for the skill (respect any
    // customizations); replace-in-place for the CLAUDE.md sentinel
    // block and the explicit .agentx-memory.md file.
    this.installAgentMemorySurface()

    // Sync each agent's MCP server config to <workspace>/.mcp.json.
    // Operator-owned files (no agentx marker) are skipped; only files
    // we wrote ourselves are rewritten or removed.
    this.installAgentMcpConfig()

    // Background-index any codegraph-enabled workspace whose `.codegraph/`
    // is missing. Fire-and-forget — boot continues immediately; indexes
    // land while the daemon is already serving traffic, and cold
    // dispatches before the index is ready fall through gracefully (the
    // managed CLAUDE.md tells the agent to use grep when codegraph
    // returns nothing).
    void bootstrapCodegraphIndexes(this.config.agents, this.log)

    // Print cron summary
    const cronJobs = this.cron.list()
    if (cronJobs.length) {
      this.log("")
      this.log("  Cron Jobs:")
      for (const job of cronJobs) {
        const status = job.enabled ? "enabled" : "disabled"
        this.log(`    ${job.id} [${status}] → ${job.agent} (${job.schedule})`)
      }
    }

    // Print mesh summary
    if (this.mesh) {
      this.log("")
      this.log("  Mesh Peers:")
      for (const peer of this.mesh.directory()) {
        const status = peer.healthy ? "✓" : "✗"
        this.log(`    ${status} ${peer.peer} (${peer.peerUrl})`)
      }
    }

    // Start config file watcher (opt-out via AGENTX_AUTO_RELOAD=false)
    if (process.env.AGENTX_AUTO_RELOAD !== "false") {
      this.startConfigWatcher()
    }

    // Drop old task-history folders past the retention window.
    try {
      const removed = this.registry.pruneTaskHistory()
      if (removed > 0) this.log(`  Pruned ${removed} old task-history folder(s)`)
    } catch { /* best-effort */ }

    // SQLite-side retention sweep — task_history / rotations / route_traces
    // grow unbounded otherwise. 90 days is generous for live debugging while
    // keeping the file small enough to rsync. Same window as task-history files.
    try {
      if (this.db) {
        const r = pruneSqliteTables(this.db, 90)
        const total = r.taskHistory + r.rotations + r.routeTraces
        if (total > 0) {
          this.log(`  Pruned ${total} old SQLite row(s) (task_history=${r.taskHistory}, rotations=${r.rotations}, route_traces=${r.routeTraces})`)
        }
      }
    } catch (e: any) {
      this.log(`  SQLite prune skipped: ${e?.message ?? e}`)
    }

    // Warm the per-agent last-summary cache from disk so dashboard cards have
    // something to show the instant the daemon boots.
    try {
      const loaded = this.registry.hydrateLastSummariesFromDisk()
      if (loaded > 0) this.log(`  Loaded last-activity summaries for ${loaded} agent(s)`)
    } catch { /* best-effort */ }

    // Built-in actions (improvement plan #6) — typed Zod-validated
    // primitives any agent can call over /api/actions/builtin/:name.
    // Idempotent registration; safe across daemon restarts in-process.
    try {
      registerAllBuiltins()
      const n = listBuiltins().length
      this.log(`  Built-in actions: ${n} registered`)
    } catch (e: any) {
      this.log(`  Built-in actions: registration failed (${e.message})`)
    }

    this.log("")
    this.log("  Ready.")
    this.log("")

    // Write PID file
    const pidFile = resolve(process.cwd(), ".agentx/daemon.pid")
    mkdirSync(dirname(pidFile), { recursive: true })
    writeFileSync(pidFile, String(process.pid))
    this.log(`  PID: ${process.pid} (${pidFile})`)

    // Catch unhandled errors — log but don't crash
    process.on("uncaughtException", (err) => {
      this.log(`UNCAUGHT EXCEPTION: ${err.message}`)
      this.log(err.stack || "")
    })
    process.on("unhandledRejection", (reason) => {
      this.log(`UNHANDLED REJECTION: ${reason}`)
    })

    // Graceful shutdown
    let stopping = false
    const shutdown = async (signal: string) => {
      if (stopping) return
      stopping = true
      this.log(`\n  Received ${signal}, shutting down gracefully...`)
      await this.stop()
    }
    process.on("SIGINT", () => shutdown("SIGINT"))
    process.on("SIGTERM", () => shutdown("SIGTERM"))
  }

  async stop(): Promise<void> {
    const start = Date.now()

    try {
      this.log("  Stopping channels...")
      await Promise.race([this.router.stopAll(), new Promise(r => setTimeout(r, 5000))])
    } catch (e: any) {
      this.log(`  Channel stop error: ${e.message}`)
    }

    // Drain active agent tasks before exit. Channels are already stopped, so
    // the active count can only shrink. This is what lets `systemctl restart`
    // survive long-running agent turns (e.g. a 3-minute coder reply) without
    // killing them mid-flight. The inflight log handles messages that hadn't
    // started yet — drain handles ones already executing.
    //
    // Drain ceiling: AGENTX_DRAIN_TIMEOUT_MS (default 300_000 = 5 min). Keep
    // systemd's TimeoutStopSec ≥ this + ~30s margin or systemd will SIGKILL
    // mid-drain.
    try {
      const drainTimeoutMs = parseInt(process.env.AGENTX_DRAIN_TIMEOUT_MS || "300000", 10)
      const drainStart = Date.now()
      const inflightCount = () => this.registry.getActiveTaskCount() + this.router.getActiveMeshForwardCount()
      let active = inflightCount()
      if (active > 0) {
        this.log(`  Draining ${active} in-flight task(s) — local=${this.registry.getActiveTaskCount()}, mesh-forwards=${this.router.getActiveMeshForwardCount()} (max ${Math.round(drainTimeoutMs / 1000)}s)...`)
        while (active > 0 && Date.now() - drainStart < drainTimeoutMs) {
          await new Promise(r => setTimeout(r, 500))
          active = inflightCount()
        }
        const elapsedMs = Date.now() - drainStart
        if (active === 0) {
          this.log(`  Drain complete (${elapsedMs}ms)`)
        } else {
          this.log(`  Drain timeout after ${elapsedMs}ms — ${active} task(s) still in flight (local=${this.registry.getActiveTaskCount()}, mesh-forwards=${this.router.getActiveMeshForwardCount()}), exiting anyway`)
        }
      }
    } catch (e: any) {
      this.log(`  Drain error: ${e.message}`)
    }

    try {
      // Persist router dedup + any debounced per-channel state before exit so
      // a clean stop doesn't drop the last in-memory window of processed ids.
      this.router.flushPersistence?.()
    } catch (e: any) {
      this.log(`  Router flush error: ${e.message}`)
    }

    try {
      if (this.processRegistry) {
        this.log("  Stopping persistent claude processes...")
        await this.processRegistry.stop()
        setProcessRegistry(null)
      }
    } catch (e: any) {
      this.log(`  ProcessRegistry stop error: ${e.message}`)
    }

    try {
      this.log("  Stopping crons (saving last run times)...")
      await this.cron.stop()
    } catch {}

    try {
      this.projectRules.stop()
    } catch {}

    try {
      this.log("  Stopping heartbeats...")
      this.heartbeat.stopAll()
    } catch {}

    try {
      if (this.business) {
        this.log("  Stopping business layer...")
        this.business.stop()
      }
    } catch {}

    try {
      if (this.mesh) {
        this.log("  Stopping mesh...")
        await this.mesh.stop()
      }
    } catch {}

    try {
      if (this.botManager) {
        this.botManager.shutdown()
      }
    } catch {}

    try {
      if (this.webrtc) {
        this.webrtc.shutdown()
      }
    } catch {}

    // Move B — dispose plugins. Each dispose() runs the plugin's optional
    // teardown() AND removes every bus subscription it attached via
    // ctx.on(). Run sequentially so a slow teardown doesn't race with
    // the daemon's own shutdown logging.
    for (const p of this.loadedPlugins) {
      try {
        await p.dispose()
      } catch (e: any) {
        this.log(`  Plugin dispose error (${p.manifest.name}): ${e?.message ?? e}`)
      }
    }

    if (this.midnightTimer) clearTimeout(this.midnightTimer)

    if (this.reloadTimer) clearTimeout(this.reloadTimer)
    if (this.configWatcher) {
      try { this.configWatcher.close() } catch { /* best effort */ }
    }

    if (this.attachSweep) clearInterval(this.attachSweep)

    if (this.httpServer) {
      this.httpServer.close()
    }

    // Clean PID file
    try {
      const pidFile = resolve(process.cwd(), ".agentx/daemon.pid")
      if (existsSync(pidFile)) unlinkSync(pidFile)
    } catch {}

    this.log(`  Shutdown complete (${Date.now() - start}ms)`)
    process.exit(0)
  }

  private midnightTimer?: ReturnType<typeof setTimeout>

  /**
   * Watch agentx.json for external edits (e.g. `agentx config set ...`) and
   * reload cron jobs + in-memory config. Channels / agents / mesh changes
   * still require a restart — we log a warning so the operator knows.
   */
  private startConfigWatcher(): void {
    const path = this.configPath || resolve(process.cwd(), "agentx.json")
    if (!existsSync(path)) return
    try {
      this.configWatcher = watch(path, { persistent: false }, (eventType) => {
        if (eventType !== "change") return
        if (this.reloadTimer) clearTimeout(this.reloadTimer)
        this.reloadTimer = setTimeout(() => {
          this.reload().catch((e) => this.log(`[reload] failed: ${e?.message || e}`))
        }, 500) // debounce
      })
      this.log(`  Watching ${path} for config changes`)
    } catch (e: any) {
      this.log(`  Config watcher failed to start: ${e.message}`)
    }
  }

  /**
   * Re-read agentx.json, diff against the in-memory config, apply what we
   * can hot-reload (crons, notify-destination, business metadata), and warn
   * about sections that require a daemon restart (channels, agents, mesh,
   * node.bind, providers).
   */
  async reload(): Promise<{ applied: string[]; restartRequired: string[]; error?: string }> {
    let next: DaemonConfig
    try {
      next = loadDaemonConfig(this.configPath)
    } catch (e: any) {
      this.log(`[reload] config invalid, keeping previous: ${e.message}`)
      return { applied: [], restartRequired: [], error: e.message }
    }

    const applied: string[] = []
    const restartRequired: string[] = []

    // 1. Crons — safe to hot-swap (stop + reinit)
    if (JSON.stringify(this.config.crons) !== JSON.stringify(next.crons)) {
      try {
        await this.cron.stop()
        this.cron = new CronScheduler(next, this.registry, this.hooks, this.log)
        this.cron.setNotifyCallback(async (jobId, agent, error, consecutiveErrors) => {
          this.log(`[CRON ALERT] Cron "${jobId}" failed (${consecutiveErrors}x) — ${error.slice(0, 200)}`)
          this.broadcastSSE("cron-failure", JSON.stringify({ jobId, agent, error, consecutiveErrors }))
          const cronDef = next.crons[jobId]
          if (cronDef?.notify) {
            try {
              await this.router.sendOutbound({
                channel: cronDef.notify.channel,
                chatId: cronDef.notify.chatId,
                text: `Cron "${jobId}" failed (${consecutiveErrors}x)\n${error.slice(0, 300)}`,
                agentId: agent,
                accountId: cronDef.notify.accountId,
              })
            } catch (e: any) {
              this.log(`[CRON ALERT] notify send failed: ${e.message}`)
            }
          }
        })
        await this.cron.start()
        applied.push("crons")
      } catch (e: any) {
        this.log(`[reload] cron reload failed: ${e.message}`)
      }
    }

    // 2. Telegram accounts — hot-swap where we can. Adding a new bot account
    //    (or rotating an existing token) used to need a full restart; now the
    //    adapter diffs the new map against its live account set and starts/
    //    stops poll loops surgically. Router also gets the fresh config so
    //    send-side paths resolve the new token. Policy.allowFrom flips through
    //    effectiveAllowFrom without restart.
    const tgPrev = this.config.channels?.telegram
    const tgNext = next.channels?.telegram
    const tgChanged = JSON.stringify(tgPrev) !== JSON.stringify(tgNext)
    let tgHandled = false
    if (tgChanged && tgNext?.enabled) {
      const telegram = this.router.getChannel("telegram") as TelegramAdapter | undefined
      if (telegram) {
        try {
          const diff = await telegram.reloadAccounts(tgNext.accounts, tgNext.policy)
          this.router.updateConfig(next)
          const parts: string[] = []
          if (diff.added.length) parts.push(`+${diff.added.join(",")}`)
          if (diff.removed.length) parts.push(`-${diff.removed.join(",")}`)
          if (diff.tokenChanged.length) parts.push(`~${diff.tokenChanged.join(",")}`)
          applied.push(parts.length ? `telegram(${parts.join(" ")})` : "telegram")
          tgHandled = true
        } catch (e: any) {
          this.log(`[reload] telegram hot-reload failed: ${e.message}`)
        }
      }
    } else if (tgChanged && !tgNext?.enabled && tgPrev?.enabled) {
      // enabled→disabled transition still needs a restart — the adapter and
      // the channels Map can't be torn down cleanly mid-flight.
      tgHandled = false
    } else if (tgChanged && tgNext?.enabled && !tgPrev?.enabled) {
      // disabled→enabled: adapter doesn't exist yet, needs full init path.
      tgHandled = false
    } else if (!tgChanged) {
      tgHandled = true // no change, nothing to do
    }

    // 3. Providers — hot-swap the credential table. Registry re-reads it
    //    per-task execution, so rotating API keys or adding a provider lands
    //    instantly for the next task (in-flight tasks keep their closure).
    if (JSON.stringify(this.config.providers) !== JSON.stringify(next.providers)) {
      try {
        this.registry.setProviders(next.providers)
        applied.push("providers")
      } catch (e: any) {
        this.log(`[reload] providers hot-reload failed: ${e.message}`)
      }
    }

    // 4. Mesh peers — diff the peer list: adds, removes, and url/token
    //    rotations are handled in-place and trigger an immediate rediscovery
    //    for changed peers. Health-check interval change needs restart.
    const meshPrev = this.config.mesh
    const meshNext = next.mesh
    const meshChanged = JSON.stringify(meshPrev) !== JSON.stringify(meshNext)
    let meshHandled = !meshChanged
    if (meshChanged && meshNext.enabled && meshPrev.enabled && this.mesh) {
      // Only the peers list and per-peer url/token are hot. healthCheck
      // interval/timeout changes still need a restart because we'd have to
      // re-install setInterval.
      const intervalChanged = JSON.stringify(meshPrev.healthCheck) !== JSON.stringify(meshNext.healthCheck)
      if (!intervalChanged) {
        try {
          const diff = await this.mesh.reloadPeers(next)
          const parts: string[] = []
          if (diff.added.length) parts.push(`+${diff.added.join(",")}`)
          if (diff.removed.length) parts.push(`-${diff.removed.join(",")}`)
          if (diff.updated.length) parts.push(`~${diff.updated.join(",")}`)
          applied.push(parts.length ? `mesh(${parts.join(" ")})` : "mesh")
          meshHandled = true
        } catch (e: any) {
          this.log(`[reload] mesh hot-reload failed: ${e.message}`)
        }
      }
    }

    // 5. Services — recompile the matcher's regex table. match() is sync and
    //    stateless, so a swap between iterations is safe.
    if (JSON.stringify(this.config.services) !== JSON.stringify(next.services)) {
      try {
        const matcher = this.router.getServiceMatcher()
        if (matcher) {
          const { count } = matcher.reload(next.services)
          applied.push(`services(${count})`)
        } else if (Object.keys(next.services).length > 0) {
          // Services went from empty at boot to non-empty — we never created
          // a matcher, so we do create one now and wire it into the router.
          const fresh = new ServiceMatcher(next.services, this.log)
          this.router.setServiceMatcher(fresh)
          applied.push(`services(${Object.keys(next.services).length})`)
        }
      } catch (e: any) {
        this.log(`[reload] services hot-reload failed: ${e.message}`)
      }
    }

    // 6. Hooks — clear + reload from disk. Registry is just a Map<event, defs>
    //    so the swap is atomic between events.
    try {
      const beforeSize = this.hooks.size?.() ?? 0
      this.hooks.clear()
      loadHooks(process.cwd(), this.hooks)
      const afterSize = this.hooks.size?.() ?? 0
      if (beforeSize !== afterSize) applied.push(`hooks(${afterSize})`)
    } catch (e: any) {
      this.log(`[reload] hooks reload failed: ${e.message}`)
    }

    // 7. Landscape — cheap rebuild, always safe.
    if (JSON.stringify(this.config.business) !== JSON.stringify(next.business)
        || JSON.stringify(this.config.agents) !== JSON.stringify(next.agents)) {
      try {
        this.landscape = new LandscapeBuilder(next)
        this.registry.setLandscape(this.landscape)
        applied.push("landscape")
      } catch (e: any) {
        this.log(`[reload] landscape rebuild failed: ${e.message}`)
      }
      // Phase 4: when agents change locally, kick mesh peers to re-probe
      // us — and equally re-probe THEM so their roster changes are pulled
      // into our directory without waiting up to a full health-check tick.
      // Closes the "newly-added remote agent silently unreachable for up
      // to 60s" symptom the operator reported.
      if (this.mesh && this.mesh.peerCount() > 0) {
        try {
          const refreshed = await this.mesh.refreshAll()
          const healthy = refreshed.filter(r => r.healthy).length
          applied.push(`mesh.refresh(${healthy}/${refreshed.length})`)
        } catch (e: any) {
          this.log(`[reload] mesh refresh failed: ${e.message}`)
        }
      }
    }

    // 8. Sections that still require a full restart — narrowed down to:
    //    agents (runtime state captured per-task), node.bind (listen socket),
    //    non-telegram channels (session-bound sockets), mesh.healthCheck
    //    (interval timer), and enabling/disabling a channel adapter wholesale.
    if (JSON.stringify(this.config.agents) !== JSON.stringify(next.agents)) {
      // Let registry swap the config reference so landscape/business reads
      // pick up new agent metadata (avatar, access, tier display). New
      // physical agents (adding/removing keys) still need restart because
      // the registry initializes state maps on construction.
      const oldIds = Object.keys(this.config.agents).sort().join(",")
      const newIds = Object.keys(next.agents).sort().join(",")
      if (oldIds === newIds) {
        // Same set of agent ids — hot-swap config-only fields through the
        // registry. Model changes still need a restart because Claude Code
        // subprocesses capture it at spawn; we surface this below.
        this.registry.setConfig(next)
        applied.push("agents.meta")
      }
      // Detect which specific fields changed and whether restart is needed.
      const restartFields = detectAgentRestartFields(this.config.agents, next.agents)
      if (restartFields.length > 0) {
        restartRequired.push(`agents(${restartFields.join(",")})`)
      }
    }

    if (!meshHandled) restartRequired.push("mesh")
    if (JSON.stringify(this.config.node) !== JSON.stringify(next.node)) {
      restartRequired.push("node")
    }
    // Channels: hot-handle telegram, everything else is still restart-required.
    const channelsPrevMinusTg = { ...this.config.channels, telegram: undefined }
    const channelsNextMinusTg = { ...next.channels, telegram: undefined }
    if (JSON.stringify(channelsPrevMinusTg) !== JSON.stringify(channelsNextMinusTg)) {
      restartRequired.push("channels")
    } else if (tgChanged && !tgHandled) {
      restartRequired.push("channels.telegram")
    }

    // 8a. Contact directory — re-read .agentx/contacts.json. The contacts
    //     file is independent of agentx.json (operator-managed, sibling of
    //     .agentx/sessions); reloading it here lets `agentx_send_contact`
    //     pick up edits without a daemon restart.
    {
      const before = this.contacts.size()
      const result = this.contacts.reload()
      if (result.error) {
        this.log(`[reload] contacts.json invalid: ${result.error}`)
      } else if (result.count !== before) {
        applied.push(`contacts(${result.count})`)
      }
    }

    // 8b. Webhook entries — hot-reload triggers / secretEnv / mesh routes
    //     without a daemon bounce. Phase 4 closes the recurring complaint
    //     that adding a webhook route to agentx.json required a restart.
    if (JSON.stringify(this.config.webhooks) !== JSON.stringify(next.webhooks)) {
      try {
        this.webhooks.setWebhookEntries(next.webhooks)
        applied.push(`webhooks(${next.webhooks.length})`)
      } catch (e: any) {
        this.log(`[reload] webhooks hot-reload failed: ${e.message}`)
      }
    }

    // 9. Swap in the new config so read-only endpoints (GET /crons etc.)
    //    reflect it, and router send-side paths see fresh channel config.
    this.config = next
    this.router.updateConfig(next)

    if (applied.length) this.log(`[reload] applied: ${applied.join(", ")}`)
    if (restartRequired.length) {
      this.log(`[reload] restart required to apply changes in: ${restartRequired.join(", ")}`)
      this.broadcastSSE("reload-partial", JSON.stringify({ applied, restartRequired }))
    } else if (applied.length) {
      this.broadcastSSE("reload-complete", JSON.stringify({ applied }))
    }

    return { applied, restartRequired }
  }

  private scheduleMidnightHook(): void {
    const scheduleNext = () => {
      const now = new Date()
      // Next midnight in Africa/Tunis (5s buffer to ensure day rollover)
      const tunisStr = now.toLocaleString("en-US", { timeZone: "Africa/Tunis" })
      const tunisNow = new Date(tunisStr)

      const target = new Date(tunisNow)
      target.setDate(target.getDate() + 1)
      target.setHours(0, 0, 5, 0)

      const tunisOffset = tunisNow.getTime() - now.getTime()
      const delay = target.getTime() - tunisOffset - now.getTime()

      this.log(`  Cost tracking: next run in ${Math.round(delay / 60_000)}min`)

      this.midnightTimer = setTimeout(async () => {
        await this.runDailyCostReport()
        scheduleNext()
      }, Math.max(delay, 60_000)) // min 1 minute guard
    }

    scheduleNext()
  }

  private async runDailyCostReport(): Promise<void> {
    const yesterday = new Date(Date.now() - 86400_000).toISOString().slice(0, 10)
    const tracker = this.registry.getTokenTracker()

    // Idempotent guard — catch-up at startup may have already appended this
    // date, and a timer-edge double-fire would otherwise produce duplicate
    // rows (as happened with the 2026-04-08 duplicate in the current file).
    if (tracker.hasTokenCostsEntry(yesterday)) {
      this.log(`  Cost tracking: ${yesterday} already logged, skipping`)
      return
    }

    const report = tracker.generateDailyReport(yesterday)
    if (!report || report.totalTasks === 0) {
      this.log(`  Cost tracking: no usage for ${yesterday}, skipping`)
      return
    }

    tracker.appendToTokenCosts(report)
    this.log(
      `  Cost tracking: ${yesterday} — ${report.totalTasks} tasks, $${report.totalCost.toFixed(4)} (top: ${report.topAgent} $${report.topCost.toFixed(4)})`,
    )
  }

  private async startChannels(): Promise<void> {
    // Telegram
    if (this.config.channels.telegram.enabled) {
      const accounts = this.config.channels.telegram.accounts
      if (Object.keys(accounts).length > 0) {
        const policy = this.config.channels.telegram.policy
        const telegram = new TelegramAdapter(
          accounts,
          { policy: { allowFrom: policy?.allowFrom } },
          this.log,
        )
        this.router.addChannel(telegram)
        const globalSize = policy?.allowFrom?.length ?? 0
        const closedAccts = Object.entries(accounts).filter(
          ([, c]) => !c.allowFrom && globalSize === 0,
        ).length
        if (closedAccts > 0) {
          this.log(
            `  Telegram: enabled — WARNING: ${closedAccts} account(s) have no allowFrom (global or per-account). All incoming messages will be DROPPED.`,
          )
        } else {
          this.log(`  Telegram: enabled — global allowFrom entries: ${globalSize}`)
        }
      }
    }

    // WhatsApp
    if (this.config.channels.whatsapp.enabled) {
      const { setWhatsAppQR, setWhatsAppStatus } = await import("./whatsapp-state")
      const whatsapp = new WhatsAppAdapter(
        {
          sessionDir: this.config.channels.whatsapp.sessionDir,
          defaultAgent: this.config.channels.whatsapp.defaultAgent,
          allowFrom: this.config.channels.whatsapp.allowFrom,
          routes: this.config.channels.whatsapp.routes,
          // Throttle for live Baileys reads (ingestor). Defaults in the
          // adapter are conservative; operators can tighten/loosen via
          // channels.whatsapp.ingest.throttle.
          throttle: {
            minMsBetweenCalls: this.config.channels.whatsapp.ingest?.throttle?.minMsBetweenCalls,
            maxCallsPerMinute: this.config.channels.whatsapp.ingest?.throttle?.maxCallsPerMinute,
          },
          // Publish QR + status so /api/admin/channels/whatsapp/state can
          // surface the pairing code in the browser.
          onQR: setWhatsAppQR,
          onStatus: setWhatsAppStatus,
        },
        this.log,
      )
      this.router.addChannel(whatsapp)
      this.log(`  WhatsApp: enabled (${this.config.channels.whatsapp.routes.length} routes)`)
    }

    // GitLab
    if (this.config.channels.gitlab?.enabled && this.config.channels.gitlab.token) {
      const gitlab = new GitLabAdapter(
        {
          webhookPort: this.config.channels.gitlab.webhookPort,
          webhookSecret: this.config.channels.gitlab.webhookSecret,
          host: this.config.channels.gitlab.host,
          token: this.config.channels.gitlab.token,
          routes: this.config.channels.gitlab.routes,
          agentMappings: this.config.channels.gitlab.agentMappings,
          // Every agent becomes an @-mention target on GitLab by default.
          // Explicit agentMappings above take precedence (per-agent token,
          // custom usernames); anything else gets a default derivation on
          // (re)start. Add/remove an agent → no GitLab config change needed.
          knownAgentIds: Object.keys(this.config.agents),
        },
        this.log,
        this.hooks,
      )
      // Wire up mesh reaction forwarder — for agents hosted on remote peers
      if (this.mesh) {
        // Generic cross-mesh mention resolution — when an @-mention doesn't
        // match a local agent or an explicit `node:` mapping, the adapter
        // walks `mesh.directory()` looking for a peer whose agent-card skill
        // tags include the mention. This is how a brand-new remote agent
        // becomes reachable via @-mention without operators having to mirror
        // an `agentMappings` entry on every node.
        gitlab.setMesh(this.mesh)
        gitlab.setReactForwarder(async (node, project, noteableType, noteableIid, noteId, agentId) => {
          const peer = this.mesh!.directory().find(p => p.peer === node && p.healthy)
          if (!peer) {
            this.log(`[gitlab] react forward: peer "${node}" not found or unhealthy`)
            return
          }
          const url = `${peer.peerUrl}/gitlab/react`
          try {
            const r = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...this.mesh!.authHeaders(peer.peer) },
              body: JSON.stringify({ project, noteableType, noteableIid, noteId, agentId }),
            })
            const respText = await r.text().catch(() => "")
            this.log(`[gitlab] react forward -> ${url} : ${r.status} ${respText.slice(0, 200)}`)
          } catch (e: any) {
            this.log(`[gitlab] react forward FAILED -> ${url} : ${e.message}`)
          }
        })

        // Forward note posting to peer so reply identity stays under the
        // agent's real GitLab user (e.g. @devops-noqta) instead of whatever
        // the local global token resolves to (the group-access-token bot).
        gitlab.setSendNoteForwarder(async (node, project, noteableType, noteableIid, agentId, text): Promise<string> => {
          const peer = this.mesh!.directory().find(p => p.peer === node && p.healthy)
          if (!peer) {
            this.log(`[gitlab] send-note forward: peer "${node}" not found or unhealthy`)
            return ""
          }
          const url = `${peer.peerUrl}/gitlab/send-note`
          try {
            const r = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...this.mesh!.authHeaders(peer.peer) },
              body: JSON.stringify({ project, noteableType, noteableIid, agentId, text }),
            })
            const data = await r.json().catch(() => ({}))
            this.log(`[gitlab] send-note forward -> ${url} : ${r.status} noteId=${(data as any).noteId || "?"}`)
            return (data as any).noteId || ""
          } catch (e: any) {
            this.log(`[gitlab] send-note forward FAILED -> ${url} : ${e.message}`)
            return ""
          }
        })

        // Same for time tracking — peer posts /add_spent_time as its own user.
        gitlab.setLogTimeForwarder(async (node, project, noteableType, noteableIid, agentId, durationMs): Promise<void> => {
          const peer = this.mesh!.directory().find(p => p.peer === node && p.healthy)
          if (!peer) {
            this.log(`[gitlab] log-time forward: peer "${node}" not found or unhealthy`)
            return
          }
          const url = `${peer.peerUrl}/gitlab/log-time`
          try {
            const r = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...this.mesh!.authHeaders(peer.peer) },
              body: JSON.stringify({ project, noteableType, noteableIid, agentId, durationMs }),
            })
            const respText = await r.text().catch(() => "")
            this.log(`[gitlab] log-time forward -> ${url} : ${r.status} ${respText.slice(0, 160)}`)
          } catch (e: any) {
            this.log(`[gitlab] log-time forward FAILED -> ${url} : ${e.message}`)
          }
        })
      }
      gitlab.setProjectRules(this.projectRules)
      this.router.addChannel(gitlab)
      this.log(`  GitLab: enabled (${this.config.channels.gitlab.routes.length} project routes, webhook :${this.config.channels.gitlab.webhookPort})`)
    }

    // GitHub
    if (this.config.channels.github?.enabled) {
      const githubConfig = this.config.channels.github
      this.github = new GitHubAdapter(
        {
          token: githubConfig.token,
          tokenFile: githubConfig.tokenFile,
          appId: githubConfig.appId,
          clientId: githubConfig.clientId,
          privateKeyFile: githubConfig.privateKeyFile,
          webhookSecret: githubConfig.webhookSecret,
          routes: githubConfig.routes,
          agentMappings: githubConfig.agentMappings,
        },
        this.log,
      )
      // Wire mesh comment forwarder for remote agents
      if (this.mesh) {
        this.github.setSendCommentForwarder(async (node, repo, issueNumber, agentId, text): Promise<string> => {
          const peer = this.mesh!.directory().find(p => p.peer === node && p.healthy)
          if (!peer) {
            this.log(`[github] send-comment forward: peer "${node}" not found or unhealthy`)
            return ""
          }
          const url = `${peer.peerUrl}/github/send-comment`
          try {
            const r = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...this.mesh!.authHeaders(peer.peer) },
              body: JSON.stringify({ repo, issueNumber, agentId, text }),
            })
            const data = await r.json().catch(() => ({}))
            this.log(`[github] send-comment forward -> ${url} : ${r.status} commentId=${(data as any).commentId || "?"}`)
            return (data as any).commentId || ""
          } catch (e: any) {
            this.log(`[github] send-comment forward FAILED -> ${url} : ${e.message}`)
            return ""
          }
        })
      }
      this.github.setProjectRules(this.projectRules)
      this.router.addChannel(this.github)
      this.log(`  GitHub: enabled (${githubConfig.routes.length} repo routes)`)
    }

    // WebRTC signaling — control plane only. Media flows browser-to-browser
    // via WebRTC direct, never through this daemon. See src/channels/webrtc-signal.ts.
    if (this.config.channels.webrtc?.enabled) {
      const wrtcCfg = this.config.channels.webrtc
      this.webrtc = new WebRtcSignalBroker(
        this.config.node.name,
        wrtcCfg.allowedCallers,
        this.log,
      )
      if (this.mesh) {
        this.webrtc.setForwarder(async (peer, signal) => {
          try {
            return await this.mesh!.sendSignal(peer, signal)
          } catch (e: any) {
            this.log(`[webrtc] forward to "${peer}" failed: ${e.message}`)
            return false
          }
        })
      }
      // Ring notifications: when a remote peer rings us, emit a "tap to join"
      // message through each configured channel so the callee actually sees
      // the incoming call even if their browser isn't open.
      if (wrtcCfg.ringNotify.length > 0) {
        const urlBase = wrtcCfg.callUrlBase || `http://${this.config.node.bind}`
        this.webrtc.setRingHandler(async (signal) => {
          const link = `${urlBase}/call?to=${encodeURIComponent(signal.from)}&callId=${encodeURIComponent(signal.callId)}`
          const text = `📞 ${signal.from} is calling — tap to join: ${link}`
          for (const target of wrtcCfg.ringNotify) {
            try {
              await this.router.sendOutbound({
                channel: target.channel,
                chatId: target.chatId,
                text,
                parseMode: "plain",
                ...(target.accountId ? { accountId: target.accountId } : {}),
              })
            } catch (e: any) {
              this.log(`[webrtc] ring notify via ${target.channel}:${target.chatId} failed: ${e.message}`)
            }
          }
        })
      }
      // AI participant ("bot") — server-side WebRTC peer that joins on
      // ?bot=<id>, transcribes remote audio, posts chunks to a channel.
      if (wrtcCfg.bot.enabled) {
        const botCfg = wrtcCfg.bot
        const iceServers: RTCIceServer[] = [
          ...wrtcCfg.stunServers.map(urls => ({ urls })),
          ...wrtcCfg.turnServers,
        ]
        this.botManager = new BotManager({
          broker: this.webrtc,
          iceServers,
          whisperBackend: botCfg.whisperBackend,
          whisperModel: botCfg.whisperModel,
          whisperLanguage: botCfg.whisperLanguage,
          mlxBinary: botCfg.mlxBinary,
          maxCallMinutes: botCfg.maxCallMinutes,
          log: this.log,
          onTranscript: async ({ invite, text, durationMs }) => {
            const dest = botCfg.transcriptChannel
            if (!dest) return
            const stamp = `[${new Date().toLocaleTimeString()} • ${(durationMs / 1000).toFixed(1)}s • ${invite.target}]`
            try {
              await this.router.sendOutbound({
                channel: dest.channel,
                chatId: dest.chatId,
                text: `${stamp}\n${text}`,
                parseMode: "plain",
                ...(dest.accountId ? { accountId: dest.accountId } : {}),
              })
            } catch (e: any) {
              this.log(`[bot-manager] transcript send via ${dest.channel}:${dest.chatId} failed: ${e.message}`)
            }
          },
        })
        this.log(`  WebRTC bot: enabled (whisper=${botCfg.whisperBackend}, default-agent=${botCfg.defaultAgentId || "(none)"}, transcript=${botCfg.transcriptChannel ? `${botCfg.transcriptChannel.channel}:${botCfg.transcriptChannel.chatId}` : "(none)"})`)
      }
      this.log(`  WebRTC signaling: enabled (stun=${wrtcCfg.stunServers.length}, turn=${wrtcCfg.turnServers.length}, allowedCallers=${wrtcCfg.allowedCallers.length || "all"}, ringNotify=${wrtcCfg.ringNotify.length}, bot=${wrtcCfg.bot.enabled ? "on" : "off"})`)
    }

    // Workflow engine — register hook subscribers BEFORE startAll so a
    // webhook arriving immediately sees an engine ready to evaluate. Skipped
    // when `workflows.enabled` is false to keep existing installs silent.
    await this.bootWorkflowEngine()

    // Wire SessionStore to the channel adapters so cold-create sessions can
    // call adapter.seedHistory() and mirror the live channel before the
    // first turn renders. Installed AFTER all addChannel calls (above) so
    // the resolver sees the full channel set.
    this.registry.getSessionStore().setAdapterResolver((channel) => this.router.getChannel(channel))

    await this.router.startAll()
  }

  /** Wires the workflow dispatcher + hook subscribers against the running
   *  daemon. No-op when `workflows.enabled` is false — the observability
   *  page + editor still work (they read/write disk directly via the
   *  board-dashboard routes), but no transitions fire. */
  private async bootWorkflowEngine(): Promise<void> {
    const cfg = this.config.workflows
    if (!cfg?.enabled) {
      this.log("  Workflows: disabled (set workflows.enabled to turn the engine on)")
      return
    }

    const store = new WorkflowStore({ baseDir: resolve(process.cwd(), cfg.dir) })
    const runs = new WorkflowRunStore({ baseDir: resolve(process.cwd(), cfg.dir), nodeId: this.config.node.id })

    // channels record: name -> adapter instance. Node handlers narrow to the
    // specific method they need (send / createIssue / logTimeSpent / ...).
    const channels: Record<string, unknown> = {}
    for (const name of this.router.getChannelNames()) {
      const adapter = this.router.getChannel(name)
      if (adapter) channels[name] = adapter
    }

    // Agent-execute shim for the `agent` node handler. Awaits AgentRegistry
    // inside the walk loop — the walk itself runs in a background async
    // context detached from the webhook response, so long-running agent
    // calls don't block webhook delivery.
    const agents = {
      execute: async (req: AgentExecuteRequest): Promise<AgentExecuteResponse> => {
        const start = Date.now()
        try {
          // Scope each workflow run to its own session bucket so concurrent
          // runs of the same workflow (or even different workflows hitting
          // the same agent) don't collide in api:default. Without this,
          // every workflow turn for `coder-agent` ends up in
          // `coder-agent:api:default:<day>.json` regardless of which run
          // produced it — bleeding context across runs and breaking the
          // single-conversation guarantee per workflow run.
          const wfChatId = req.workflowRunId ? `workflow:${req.workflowRunId}` : "workflow:adhoc"
          const resp = await this.registry.execute({
            agentId: req.agentId,
            message: req.message,
            workflowRunId: req.workflowRunId,
            timeoutMinutes: req.timeoutMinutes,
            context: {
              channel: "workflow",
              chatId: wfChatId,
              sender: "workflow",
            },
          })
          return {
            content: resp.content ?? "",
            error: resp.error,
            errorKind: resp.errorKind,
            taskId: `wf-${req.workflowRunId ?? "na"}-${start.toString(36)}`,
            durationMs: Date.now() - start,
          }
        } catch (e: any) {
          return { content: "", error: e.message }
        }
      },
    }

    // Mesh forwarder: when an event arrives here but the run is home'd on
    // another peer, POST directly to the peer's /workflow/event endpoint.
    // Also handles trigger fan-out: a fresh channel event arriving locally
    // is broadcast to every healthy peer so peer-side workflows with
    // `mesh.allowRemote: true` can match. Each receiver runs its own dispatch
    // with `fromRemote` set, scoping the match to opted-in workflows.
    const forwarder: WorkflowMeshForwarder | undefined = this.mesh ? {
      forwardTransition: async (peerName, payload) => {
        const peer = this.mesh!.directory().find((p) => p.peer === peerName && p.healthy)
        if (!peer) {
          this.log(`[workflows] forward to "${peerName}" skipped — peer not found or unhealthy`)
          return
        }
        const url = `${peer.peerUrl}/workflow/event`
        try {
          const r = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json", ...this.mesh!.authHeaders(peer.peer) },
            body: JSON.stringify({ payload }),
          })
          if (!r.ok) this.log(`[workflows] forward ${url} -> ${r.status}`)
        } catch (e: any) {
          this.log(`[workflows] forward ${url} failed: ${e.message}`)
        }
      },
      broadcastTrigger: async (payload) => {
        const peers = this.mesh!.directory().filter((p) => p.healthy)
        if (peers.length === 0) return
        const localPeerName = this.config.node.id
        // Visibility: emit one summary line per broadcast so operators can see
        // a channel event leaving the originating node and trace the round
        // trip (look for a matching `[workflows/mesh] received trigger ...`
        // on the receiving peer).
        this.log(`[workflows/mesh] broadcasting trigger source="${payload.trigger.source}" chat="${payload.trigger.chat ?? "*"}" -> ${peers.length} peer(s): ${peers.map((p) => p.peer).join(", ")}`)
        await Promise.allSettled(peers.map(async (peer) => {
          const url = `${peer.peerUrl}/workflow/event`
          try {
            const r = await fetch(url, {
              method: "POST",
              headers: { "Content-Type": "application/json", ...this.mesh!.authHeaders(peer.peer) },
              // Receiver discriminates on `kind: "trigger"` and dispatches
              // with fromRemote.peer = the originating node id. Peers without
              // an opted-in workflow no-op cleanly.
              body: JSON.stringify({ kind: "trigger", fromPeer: localPeerName, payload }),
              signal: AbortSignal.timeout(5000),
            })
            if (!r.ok) this.log(`[workflows/mesh] broadcastTrigger ${peer.peer} -> ${r.status}`)
          } catch (e: any) {
            this.log(`[workflows/mesh] broadcastTrigger ${peer.peer} failed: ${e.message}`)
          }
        }))
      },
      forwardChannelSend: async (payload) => {
        // Find a healthy peer that hosts this channel. Channels are typically
        // unique to a node (whatsapp on clawd-server, telegram on macbook),
        // so we just take the first hit. If multiple peers somehow host the
        // same channel, this picks deterministically by directory order.
        const peers = this.mesh!.directory().filter((p) => p.healthy && p.channels?.includes(payload.channel))
        if (peers.length === 0) {
          throw new Error(`no healthy mesh peer hosts channel "${payload.channel}"`)
        }
        const target = peers[0]
        const url = `${target.peerUrl}/channel/send`
        const r = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", ...this.mesh!.authHeaders(target.peer) },
          body: JSON.stringify(payload),
          signal: AbortSignal.timeout(15000),
        })
        if (!r.ok) {
          const txt = await r.text().catch(() => "")
          throw new Error(`peer ${target.peer} /channel/send -> ${r.status} ${txt.slice(0, 200)}`)
        }
        const body = await r.json().catch(() => ({})) as { messageId?: string | null }
        return { messageId: body.messageId ?? null }
      },
    } : undefined

    const { TimerService } = await import("@/workflows/timers")
    const timerService = new TimerService({
      baseDir: cfg.dir ? resolve(process.cwd(), cfg.dir) : undefined,
      log: (m) => this.log(m),
    })

    const dispatcher = new WorkflowDispatcher({
      store, runs,
      nodeId: this.config.node.id,
      channels,
      agents,
      forwarder,
      timers: timerService,
      events: this.events,
      log: (m) => this.log(m),
    })

    // Start the tick loop now that the dispatcher has registered its
    // resume-on-fire callback. Timers persisted from a prior run will be
    // picked up on the first tick.
    timerService.start()

    // Subscribe the built-in hook handlers.
    const handlers = createWorkflowHookHandlers(dispatcher)
    for (const [event, handler] of Object.entries(handlers)) {
      if (!handler) continue
      this.hooks.registerHandler(event as any, `workflows:${event}`, handler, 50)
    }

    // Stash for the mesh receiver endpoint and for manual-run RPC — see the
    // HTTP handler in startHttpApi where /workflow/transition arrives.
    this.workflowDispatcher = dispatcher
    this.workflowStore = store
    this.workflowRuns = runs
    // Phase 3: webhook handler can now dispatch workflows per event-type
    // (webhooks[].triggers map). When `triggers` is unset, behavior is
    // unchanged from prior versions.
    this.webhooks.setWorkflowDispatcher(dispatcher)

    // Wire the workflow auto-runner so AgentRegistry can fire matched
    // workflows directly when `workflows.matching.mode === "auto"`. The
    // runtime contract for auto-matched runs is: "the workflow gets the
    // typed inputs it declared in trigger.config.inputSchema, owns the
    // reply via its own action.send / agent nodes."
    //
    // Use dispatchWorkflow (not dispatch) so matched-by-id workflows fire
    // regardless of their trigger node's source. Drafts emitted by absorb
    // use trigger.manual without a cfg.source — dispatch's matchByTrigger
    // requires strict cfg.source === t.source which would silently drop
    // every absorbed workflow.
    //
    // Input resolution: parse chatId for project/id/ref, fill schema
    // defaults, check required. If a required field is still missing,
    // throw a typed error — the registry catches it and falls back to
    // normal agent execution (suggest mode behaviour). Operators can
    // still fill the gaps via a manual run from the dashboard.
    this.registry.setWorkflowAutoRunner(async ({ workflowId, channel, chatId, payload }) => {
      const wf = store.get(workflowId)
      if (!wf) throw new Error(`auto-run target workflow not found: ${workflowId}`)

      const resolution = resolveAutoRunInputs(wf, {
        chatId,
        channel,
        message: typeof payload.message === "string" ? payload.message : undefined,
        agentId: typeof payload.agentId === "string" ? payload.agentId : undefined,
        senderId: typeof payload.senderId === "string" ? payload.senderId : undefined,
        senderUsername: typeof payload.senderUsername === "string" ? payload.senderUsername : undefined,
      })
      if (resolution.missing.length > 0) {
        throw new Error(
          `inputSchema requires ${resolution.missing.join(", ")} — chatId+defaults filled ` +
          `[chatId: ${resolution.filledFrom.chatId.join(",") || "—"} | defaults: ${resolution.filledFrom.defaults.join(",") || "—"}]; ` +
          `auto-run skipped (operator can fill manually or extend the inputSchema).`
        )
      }
      this.log(
        `[workflows] auto-run input resolution for ${workflowId}: ` +
        `passthrough=[${resolution.filledFrom.passthrough.join(",")}] ` +
        `chatId=[${resolution.filledFrom.chatId.join(",")}] ` +
        `defaults=[${resolution.filledFrom.defaults.join(",")}]`
      )

      const entityRef = { backend: "manual", id: chatId || `auto-${Date.now().toString(36)}` }
      const eventId = `auto:${workflowId}:${chatId}:${Date.now()}`
      const result = await dispatcher.dispatchWorkflow({
        workflowId,
        entityRef,
        event: { id: eventId, payload: resolution.inputs },
        trigger: { source: "manual" },
      })
      return { runId: result.run?.id }
    })

    // Phase 3: wire trigger.cron timers + trigger.hook subscribers for
    // workflows that declare them. Channel-triggered workflows (gitlab-issue,
    // whatsapp-message, ...) are already wired via the hooks registered
    // above.
    const { cronTimers, hookSubscribers } = startWorkflowTriggers({
      store, dispatcher, hooks: this.hooks, log: (m) => this.log(m),
    })

    const count = store.list().length
    this.log(`  Workflows: enabled (${count} definition${count === 1 ? "" : "s"} loaded from ${cfg.dir}, editor=${cfg.editor}, cron=${cronTimers}, hook=${hookSubscribers})`)

    // Static dispatch-graph analysis. v0 only logs — auto-quarantine + admin
    // surface land in follow-up turns. Surfacing the conflicts at boot already
    // tells operators which workflows are racing against existing dispatch
    // paths (e.g. gitlab agentMappings) so they can flip state to `disabled`
    // by hand until the auto-fix layer ships.
    try {
      const { detectConflicts } = await import("@/workflows/conflict-detector")
      const conflicts = detectConflicts(store.list(), this.config)
      if (conflicts.length > 0) {
        this.log(`  Workflows: ${conflicts.length} dispatch conflict(s) detected`)
        for (const c of conflicts) {
          this.log(`    [${c.severity}] ${c.workflowId}: ${c.summary}`)
          this.log(`      → ${c.suggestion}`)
        }
      }
    } catch (e: any) {
      this.log(`  Workflows: conflict-detector failed: ${e.message}`)
    }
  }

  private async startHttpApi(): Promise<void> {
    const [host, portStr] = this.config.node.bind.split(":")
    const port = parseInt(portStr || "18800", 10)

    this.httpServer = createServer(async (req, res) => {
      res.setHeader("Access-Control-Allow-Origin", "*")
      res.setHeader("Access-Control-Allow-Methods", "GET, POST, OPTIONS")
      res.setHeader("Access-Control-Allow-Headers", "Content-Type, Authorization")

      if (req.method === "OPTIONS") {
        res.writeHead(204)
        res.end()
        return
      }

      await this.handleHttp(req, res)
    })

    this.httpServer.on("error", (err: any) => {
      if (err.code === "EADDRINUSE") {
        this.log(`  ERROR: Port ${port} is already in use. Retrying in 5s...`)
        setTimeout(() => {
          this.httpServer?.close()
          this.httpServer?.listen(port, host || "0.0.0.0")
        }, 5000)
      } else {
        this.log(`  HTTP error: ${err.message}`)
      }
    })

    this.httpServer.listen(port, host || "0.0.0.0", () => {
      this.log(`  HTTP API: http://${host || "0.0.0.0"}:${port}`)
    })

    // Attach-mode deadlines. Sweeping on an interval rather than scheduling a
    // timer per offered message keeps the hot path allocation-free and means
    // there are no dangling handles to clean up on shutdown. 1s granularity
    // against a 90s claim deadline is well inside the noise.
    this.attachSweep = setInterval(() => {
      try {
        const reg = getAttachRegistry()
        reg.sweep()
        reg.prune()
      } catch { /* best-effort */ }
    }, 1000)
    this.attachSweep.unref?.()
  }

  /**
   * Broadcast an SSE event to all connected clients.
   */
  private broadcastSSE(event: string, data: string): void {
    if (this.sseClients.size === 0) return
    const payload = `event: ${event}\ndata: ${JSON.stringify({ time: new Date().toISOString(), message: data })}\n\n`
    for (const client of this.sseClients) {
      try { client.write(payload) } catch { this.sseClients.delete(client) }
    }
  }

  /**
   * Handle SSE connection for live event streaming.
   * GET /events — streams daemon logs in real-time.
   */
  private handleSSE(req: IncomingMessage, res: ServerResponse): void {
    res.writeHead(200, {
      "Content-Type": "text/event-stream",
      "Cache-Control": "no-cache",
      "Connection": "keep-alive",
      "Access-Control-Allow-Origin": "*",
    })

    // Parse filter query-string. ?type=run,task filters which event
    // kinds are forwarded; ?workflow=, ?actor=, ?run=, ?channel=
    // narrow further. Empty query = unfiltered (all kinds forward).
    const url = new URL(req.url || "/", `http://${req.headers.host || "_"}`)
    const kinds = parseKindsParam(url.searchParams.get("type"))
    const filter = {
      kinds,
      workflowId: url.searchParams.get("workflow") || undefined,
      actor: url.searchParams.get("actor") || undefined,
      runId: url.searchParams.get("run") || undefined,
      channel: url.searchParams.get("channel") || undefined,
    }

    // Initial "status" snapshot — matches legacy /events shape so existing
    // consumers (dashboard status widget) keep working even after this
    // upgrade.
    const agents = this.registry.list()
    const active = agents.filter(a => a.active > 0)
    res.write(`event: status\ndata: ${JSON.stringify({
      node: this.config.node.name,
      agents: agents.length,
      active: active.map(a => ({ id: a.id, name: a.name, tasks: a.active })),
      mesh: this.mesh?.directory().map(p => ({ peer: p.peer, healthy: p.healthy })) || [],
    })}\n\n`)

    this.sseClients.add(res)

    // Bridge EventBus → this SSE client. Events that don't match the
    // filter are dropped before serialisation.
    const unsubscribe = this.events.subscribe(filter, (e) => {
      try { res.write(`event: ${e.kind}\ndata: ${JSON.stringify(e)}\n\n`) }
      catch { /* client disconnected — unsubscribe below fires on close */ }
    })

    req.on("close", () => {
      unsubscribe()
      this.sseClients.delete(res)
    })
  }

  /**
   * Stream a single running task's live output as SSE.
   * Sends a `start` event with the existing buffer (so a late opener catches up),
   * then `chunk` events for every new delta, and `end` when the task finishes.
   */
  private handleTaskStream(req: IncomingMessage, res: ServerResponse, _agentId: string, taskId: string): void {
    res.writeHead(200, {
      "Content-Type": "text/event-stream",
      "Cache-Control": "no-cache",
      "Connection": "keep-alive",
      "Access-Control-Allow-Origin": "*",
      "X-Accel-Buffering": "no",
    })
    let closed = false
    const send = (ev: string, data: unknown) => {
      if (closed) return
      try { res.write(`event: ${ev}\ndata: ${JSON.stringify(data)}\n\n`) } catch { closed = true }
    }
    // Heartbeat so proxies don't kill idle SSE connections.
    const heartbeat = setInterval(() => { if (!closed) try { res.write(": ping\n\n") } catch { /* */ } }, 15000)
    let sub: { initial: string; done: boolean; unsubscribe: () => void } | null = null
    const finish = (reason: string) => {
      if (closed) return
      send("end", { reason })
      closed = true
      clearInterval(heartbeat)
      try { sub?.unsubscribe() } catch { /* */ }
      try { res.end() } catch { /* */ }
    }
    sub = this.registry.subscribeToTaskOutput(taskId, (chunk) => {
      send("chunk", { text: chunk })
      if (chunk === "\n[task finished]\n") finish("completed")
    })
    if (!sub) {
      send("error", { message: "task not found or already evicted" })
      res.end()
      return
    }
    send("start", { taskId, initial: sub.initial, done: sub.done })
    if (sub.done) {
      send("end", { reason: "already finished" })
      res.end()
      return
    }
    req.on("close", () => {
      closed = true
      clearInterval(heartbeat)
      sub?.unsubscribe()
    })
  }

  /**
   * SSE stream for WebRTC signaling. Browser connects here identifying itself
   * with (callId, as). The broker fans out any signal addressed `to=<as>` on
   * this `callId` to the stream.
   */
  private handleWebRtcSSE(req: IncomingMessage, res: ServerResponse, url: URL): void {
    if (!this.webrtc) {
      this.json(res, 404, { error: "WebRTC signaling not enabled" })
      return
    }
    const callId = url.searchParams.get("callId")
    const as = url.searchParams.get("as")
    if (!callId || !as) {
      this.json(res, 400, { error: "Missing ?callId= and ?as=" })
      return
    }
    res.writeHead(200, {
      "Content-Type": "text/event-stream",
      "Cache-Control": "no-cache",
      "Connection": "keep-alive",
      "Access-Control-Allow-Origin": "*",
      "X-Accel-Buffering": "no",
    })
    res.write(`event: ready\ndata: ${JSON.stringify({ callId, as })}\n\n`)
    const unsubscribe = this.webrtc.subscribe(callId, as, res)
    // Proxies kill idle SSE; ping every 15s.
    const heartbeat = setInterval(() => {
      try { res.write(": ping\n\n") } catch { /* best effort */ }
    }, 15000)
    req.on("close", () => {
      clearInterval(heartbeat)
      unsubscribe()
    })
  }

  /**
   * Serve the minimal browser call page. Static HTML; no framework.
   * The page does getUserMedia, RTCPeerConnection, and POSTs/listens signals.
   */
  private serveCallPage(res: ServerResponse): void {
    res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
    res.end(CALL_PAGE_HTML)
  }

  /** POST endpoints that mesh peers call across the network. Everything
   *  else on this server is either read-only, loopback-operated (CLI,
   *  same-host dashboard), or carries its own secret (chat routes). */
  private static readonly MESH_PROTECTED_PATHS = new Set([
    "/task",
    "/mesh/task",
    "/workflow/event",
    "/workflow/transition",
    "/channel/send",
    "/webrtc/signal",
    // Peer-identity forwards: these make the daemon act as its own GitLab/
    // GitHub bot user, so an unauthenticated non-loopback caller could post
    // as the bot. Daemon-side callers attach mesh.authHeaders(peer).
    "/gitlab/react",
    "/gitlab/send-note",
    "/gitlab/log-time",
    "/github/send-comment",
  ])

  /** Wraps decideMeshAuth (daemon/mesh-auth.ts) with token collection,
   *  logging, and the 401 response. */
  private checkMeshAuth(req: IncomingMessage, res: ServerResponse, path: string): boolean {
    const accepted = new Set<string>()
    if (process.env.MESH_TOKEN) accepted.add(process.env.MESH_TOKEN)
    for (const p of this.config.mesh?.peers || []) if (p.token) accepted.add(p.token)
    if (this.config.dashboard?.token) accepted.add(this.config.dashboard.token)

    const addr = req.socket?.remoteAddress || ""
    const decision = decideMeshAuth({
      remoteAddress: addr,
      authorizationHeader: String(req.headers["authorization"] || ""),
      acceptedTokens: accepted,
      enforcementDisabled: process.env.AGENTX_MESH_AUTH === "off",
    })

    if (decision.allowed) {
      if (decision.reason === "no-tokens-configured") {
        this.log(`[auth] ⚠ unauthenticated ${path} from ${addr} allowed — no MESH_TOKEN or peer token configured. Run "agentx connect mesh" to create one; unauthenticated mesh calls will be rejected in a future release.`)
      }
      return true
    }

    this.log(`[auth] ✗ rejected ${path} from ${addr} — missing or invalid mesh token`)
    this.json(res, 401, { error: "Unauthorized: mesh token required (Authorization: Bearer <MESH_TOKEN>)" })
    return false
  }

  private async handleHttp(req: IncomingMessage, res: ServerResponse): Promise<void> {
    const url = new URL(req.url || "/", `http://${req.headers.host || "localhost"}`)
    const path = url.pathname

    try {
      if (req.method === "POST" && AgentXDaemon.MESH_PROTECTED_PATHS.has(path)) {
        if (!this.checkMeshAuth(req, res, path)) return
      }

      // Destructive-action guard (PreToolUse hook). Loopback ONLY: the hook
      // always runs on this host, and the verdict text names protected
      // hostnames, so it must never be reachable off-box. Answering here
      // instead of spawning `agentx guard check` keeps the per-tool-call
      // cost at ~1ms instead of ~300ms of node boot.
      if (req.method === "POST" && path === "/guard/check") {
        if (!isLoopback(req.socket?.remoteAddress || "")) {
          this.json(res, 403, { error: "Forbidden: /guard/check is loopback-only" })
          return
        }
        const agentId = url.searchParams.get("agent") || undefined
        const envScope = url.searchParams.get("env") || undefined
        const payload = await readBody(req).catch(() => ({} as Record<string, unknown>))
        const { stdout } = checkPayload(payload as PreToolUsePayload, {
          root: process.cwd(),
          agentId,
          env: envScope,
        })
        // Empty body == allow. The hook pipes our stdout straight to Claude Code.
        res.writeHead(200, { "Content-Type": "application/json" })
        res.end(stdout)
        return
      }

      // Attach mode (wearable agents). Same shape and same constraints as
      // /guard/check: the hooks always run on this host, the responses name
      // agent identities and quote channel messages, and a bound session can
      // answer as a production agent. Loopback ONLY.
      if (req.method === "POST" && path.startsWith("/attach/")) {
        if (!isLoopback(req.socket?.remoteAddress || "")) {
          this.json(res, 403, { error: `Forbidden: ${path} is loopback-only` })
          return
        }
        const payload = (await readBody(req).catch(() => ({}))) as HookPayload
        let out = ""
        switch (path) {
          case "/attach/session-start":
            out = onSessionStart(payload)
            break
          case "/attach/prompt":
            out = onPrompt(payload)
            break
          case "/attach/stop":
            out = onStop(payload)
            break
          case "/attach/session-end":
            out = onSessionEnd(payload)
            break
          case "/attach/bind": {
            // Control-plane, not a hook: `agentx attach` posts here.
            const reg = getAttachRegistry()
            const sessionId = String((payload as any).sessionId || "")
            const agentId = String((payload as any).agentId || "")
            const mode = (payload as any).mode
            if (!sessionId || !agentId) {
              this.json(res, 400, { error: "sessionId and agentId are required" })
              return
            }
            if (mode !== undefined && !isDeliveryMode(mode)) {
              this.json(res, 400, { error: `invalid mode: ${mode}` })
              return
            }
            if (!this.registry.getAgent(agentId)) {
              this.json(res, 404, { error: `Unknown agent: ${agentId}` })
              return
            }
            reg.register(sessionId, { cwd: (payload as any).cwd })
            const session = reg.bind(sessionId, agentId, mode)
            this.json(res, 200, { ok: true, session, pending: reg.pendingCount(sessionId) })
            return
          }
          case "/attach/next": {
            // Explicit drain — what `/inbox` and manual/notify modes use. The
            // Stop hook's auto-capture still applies afterwards, so the
            // session answers by simply replying.
            const reg = getAttachRegistry()
            const sessionId = String((payload as any).sessionId || "")
            if (!sessionId) {
              this.json(res, 400, { error: "sessionId is required" })
              return
            }
            const item = reg.claim(sessionId)
            this.json(res, 200, {
              item: item ?? null,
              pending: reg.pendingCount(sessionId),
            })
            return
          }
          case "/attach/answer": {
            const reg = getAttachRegistry()
            const sessionId = String((payload as any).sessionId || "")
            const text = String((payload as any).text || "")
            if (!sessionId || !text) {
              this.json(res, 400, { error: "sessionId and text are required" })
              return
            }
            const item = reg.answer(sessionId, text)
            if (!item) {
              this.json(res, 409, { error: "no message is currently claimed by this session" })
              return
            }
            this.json(res, 200, { ok: true, item, pending: reg.pendingCount(sessionId) })
            return
          }
          case "/attach/detach": {
            const reg = getAttachRegistry()
            const sessionId = String((payload as any).sessionId || "")
            if (!sessionId) {
              this.json(res, 400, { error: "sessionId is required" })
              return
            }
            const session = reg.unbind(sessionId, (payload as any).agentId)
            this.json(res, 200, { ok: true, session })
            return
          }
          default:
            this.json(res, 404, { error: `Unknown attach route: ${path}` })
            return
        }
        // Empty body is Claude Code's no-op. The hook pipes this straight
        // through, so it must be a bare decision object or nothing at all.
        res.writeHead(200, { "Content-Type": "application/json" })
        res.end(out)
        return
      }

      // Attached-session inspection. Loopback-only for the same reason: the
      // response names which production identities a local terminal is
      // currently answering for.
      if (req.method === "GET" && path === "/attach/sessions") {
        if (!isLoopback(req.socket?.remoteAddress || "")) {
          this.json(res, 403, { error: "Forbidden: /attach/sessions is loopback-only" })
          return
        }
        const reg = getAttachRegistry()
        this.json(res, 200, {
          sessions: reg.list().map((s) => ({
            ...s,
            pending: reg.pendingCount(s.sessionId),
          })),
        })
        return
      }

      // SSE live event stream
      if (req.method === "GET" && path === "/events") {
        this.handleSSE(req, res)
        return
      }

      // WebRTC signaling SSE stream (browser subscribes here to receive
      // offers/answers/ICE forwarded by the daemon).
      if (req.method === "GET" && path === "/webrtc/events") {
        this.handleWebRtcSSE(req, res, url)
        return
      }

      // --- Workflow RPC endpoints ---
      //
      // /workflow/transition — mesh receiver. Peer node B forwarded a
      //   triggering event here because this node is the run's home. We
      //   just re-enter the local dispatcher with the payload.
      //
      // /workflows/:id/run — manual trigger. The CLI's `agentx workflow
      //   run <id> --input ...` POSTs here. Only workflows whose trigger
      //   source is "manual" are allowed.
      // Workflow mesh + manual-run endpoints. /workflow/event receives
      // forwarded dispatches from peers when the run is home'd here.
      // /workflows/:id/run fires a manual-triggered workflow.
      if (req.method === "POST" && (path === "/workflow/event" || path === "/workflow/transition")) {
        await this.handleWorkflowEvent(req, res)
        return
      }
      if (req.method === "POST" && path === "/channel/send") {
        await this.handleChannelSend(req, res)
        return
      }
      // Known-chats discovery. Local if this node hosts the channel; else
      // forward to a peer that does. Lets the workflow editor populate a
      // chatId picker without authors memorizing platform ids. The `local=1`
      // query param suppresses mesh fan-out so peer-to-peer recursion
      // (both nodes advertise the same channel) can't loop forever.
      const channelChatsMatch = req.method === "GET" && path.match(/^\/channels\/([^/]+)\/chats$/)
      if (channelChatsMatch) {
        const localOnly = url.searchParams.get("local") === "1"
        await this.handleChannelChats(res, decodeURIComponent(channelChatsMatch[1]), { localOnly })
        return
      }
      const manualRun = req.method === "POST" && path.match(/^\/workflows\/([^/]+)\/run$/)
      if (manualRun) {
        await this.handleWorkflowManualRun(req, res, decodeURIComponent(manualRun[1]))
        return
      }

      // POST /api/workflows/drafts/:id/replay — fire a draft as an ad-hoc
      // workflow. Looks up an input from the draft's sourceTaskIds (first
      // entry by default; caller can override via `taskId`). Lives on the
      // daemon (not in workflows-api.ts) because it needs the dispatcher
      // and the trace store, neither of which the dashboard process has.
      const draftReplay = req.method === "POST" && path.match(/^\/api\/workflows\/drafts\/([^/]+)\/replay$/)
      if (draftReplay) {
        await this.handleWorkflowDraftReplay(req, res, decodeURIComponent(draftReplay[1]))
        return
      }

      // Agent-memory API — lets running Claude Code sessions save their
      // own experiential memory from inside a Bash tool call.
      //   GET  /api/memory?agent=<id>            → list records + MEMORY.md
      //   GET  /api/memory/<id>?agent=<id>       → one record
      //   POST /api/memory  body: {agentId, type, name, description, body, append?}
      //   DELETE /api/memory/<name>?agent=<id>   → remove
      if (path.startsWith("/api/memory")) {
        if (await this.handleMemoryApi(req, res, path, url)) return
      }

      // GET /api/workflows/runs[?limit=&workflowId=] + /runs/:id
      // Runs live on the node that dispatches them (home-node). The
      // board-dashboard on another host proxies to this endpoint so
      // "Recent runs" on /workflows reflects what's actually happening.
      if (req.method === "GET" && this.workflowRuns && this.workflowStore) {
        if (path === "/api/workflows") {
          this.json(res, 200, { workflows: this.workflowStore.list() })
          return
        }
        if (path === "/api/workflows/runs") {
          const limit = Math.max(1, Math.min(500, Number(url.searchParams.get("limit") || 50)))
          const workflowId = url.searchParams.get("workflowId") || undefined
          // summary=1 strips the heavy per-run context (full webhook payloads
          // + per-node outputs — routinely 10-50KB each) and trims history to
          // the last 5 entries. Honored by the dashboard's mesh-aggregation
          // handler when fanning out to peer daemons (see board-dashboard.ts).
          const summary = url.searchParams.get("summary") === "1" || url.searchParams.get("summary") === "true"
          const runs = this.workflowRuns.list({ workflowId, limit })
          if (!summary) { this.json(res, 200, { runs }); return }
          const slim = runs.map((r) => ({
            id: r.id,
            workflowId: r.workflowId,
            workflowVersion: r.workflowVersion,
            homeNode: r.homeNode,
            status: r.status,
            pending: r.pending,
            entityRef: r.entityRef,
            history: (r.history || []).slice(-5),
            parentRunId: r.parentRunId,
            parentNodeId: r.parentNodeId,
            rootRunId: r.rootRunId,
            depth: r.depth,
            createdAt: r.createdAt,
            updatedAt: r.updatedAt,
          }))
          this.json(res, 200, { runs: slim })
          return
        }
        const runMatch = path.match(/^\/api\/workflows\/runs\/([^\/]+)$/)
        if (runMatch) {
          const run = this.workflowRuns.get(decodeURIComponent(runMatch[1]))
          if (!run) { this.json(res, 404, { error: "run not found" }); return }
          this.json(res, 200, { run })
          return
        }
      }

      // POST /chat — External web-chat endpoint (noqta.tn → clawd-server).
      //
      // Contract:
      //   Authorization: Bearer <AGENTX_CHAT_SECRET>
      //   Body: { agentId, user_id, conversation_id, message, max_turns? }
      //   → { reply, tokens?, task?, queued_task?, artifacts? }
      //
      // Sentinels the agent may embed in its reply (all stripped before returning):
      //   <agentx-task>{"summary":"…"}</agentx-task>       → queued_task
      //   <agentx-artifact>{"filename":"f","mime":"t"}</agentx-artifact> → artifacts[]
      //
      // Retrieve an artifact file via:
      //   GET /agents/:id/workspace/<filename>   (same Bearer auth)
      if (req.method === "POST" && path === "/chat") {
        // Bearer auth — AGENTX_CHAT_SECRET env, falling back to TELEGRAM_BOT_API_SECRET.
        const chatSecret = process.env.AGENTX_CHAT_SECRET || process.env.TELEGRAM_BOT_API_SECRET
        if (chatSecret) {
          const authHeader = req.headers["authorization"] || ""
          const token = authHeader.startsWith("Bearer ") ? authHeader.slice(7).trim() : ""
          if (token !== chatSecret) {
            this.json(res, 401, { error: "unauthorized" }); return
          }
        }

        let body: any
        try { body = await readJsonBody(req) } catch (e: any) {
          this.json(res, 400, { error: "invalid JSON body", message: (e as Error).message }); return
        }

        const agentId = typeof body?.agentId === "string" ? body.agentId : this.config.node.defaultAgent
        const conversationId = typeof body?.conversation_id === "string" ? body.conversation_id : ""
        const userId = typeof body?.user_id === "string" ? body.user_id : "anonymous"
        const message = typeof body?.message === "string" ? body.message : ""

        if (!agentId) { this.json(res, 400, { error: "agentId required (or set node.defaultAgent)" }); return }
        if (!message)  { this.json(res, 400, { error: "message required" }); return }
        if (!conversationId) { this.json(res, 400, { error: "conversation_id required" }); return }

        // System prompt addendum injected for every web-chat turn: instructs the
        // agent to declare files it writes so the caller can surface them.
        const chatSystemAppend = [
          "## Web-chat artifact protocol",
          "When you write, generate, or save a file during this conversation, declare it",
          "at the END of your reply using this exact XML sentinel (one per file):",
          '  <agentx-artifact>{"filename":"<name.ext>","mime":"<mime/type>"}</agentx-artifact>',
          "Do NOT describe the attachment in prose — the caller will fetch and render it.",
          "Use accurate MIME types: image/png, image/jpeg, application/pdf, text/csv, etc.",
        ].join("\n")

        // Stream mode: caller wants OpenAI-shaped SSE chunks back instead
        // of a single JSON response. Triggered by body.stream:true or by
        // an explicit Accept: text/event-stream. Used by the noqta.tn
        // voice forwarder (ElevenLabs custom-LLM has a tight first-token
        // deadline that the non-streaming path blows past).
        //
        // The agentic loop still runs end-to-end on this server — we just
        // stream text deltas back as the underlying provider emits them
        // (via registry.execute's onDelta), and emit artifact/task
        // sentinels in trailing chunks the OpenAI SSE shape ignores.
        const acceptHeader = String(req.headers["accept"] || "")
        const wantStream = body?.stream === true || acceptHeader.includes("text/event-stream")

        if (wantStream) {
          res.writeHead(200, {
            "Content-Type": "text/event-stream; charset=utf-8",
            "Cache-Control": "no-cache, no-store, must-revalidate",
            Connection: "keep-alive",
            "Access-Control-Allow-Origin": "*",
          })
          const sseId = `chatcmpl-${newEventId()}`
          const created = Math.floor(Date.now() / 1000)
          const baseChunk = { id: sseId, object: "chat.completion.chunk", created, model: agentId }
          const send = (delta: Record<string, unknown>, finish: string | null = null) => {
            const chunk = { ...baseChunk, choices: [{ index: 0, delta, finish_reason: finish }] }
            res.write(`data: ${JSON.stringify(chunk)}\n\n`)
          }
          send({ role: "assistant" })

          const onDelta = (delta: string) => {
            if (delta) send({ content: delta })
          }
          // Thinking goes on a separate SSE field — DeepSeek's own
          // convention. Clients can render reasoning_content in a
          // dimmed/collapsible lane and leave `content` clean. Without
          // this, every thinking token would bleed into the visible
          // chat output prefixed with `💭 `.
          const onThinking = (text: string) => {
            if (text) send({ reasoning_content: text })
          }

          try {
            const resp = await this.registry.execute({
              agentId,
              message,
              context: { channel: "web-chat", sender: userId, chatId: conversationId },
              systemPromptAppend: chatSystemAppend,
            }, onDelta, onThinking)

            if (resp.error) {
              send({ content: `\n[error] ${resp.error}` }, "stop")
              res.write("data: [DONE]\n\n")
              res.end()
              return
            }

            // Strip the sentinels from the assistant reply (same logic as
            // non-stream path). We don't backpatch what the model already
            // streamed — sentinels reach the client as raw text and the
            // client strips them too. For voice that's fine: the sentinel
            // string is short and EL's TTS reads it as gibberish but
            // doesn't break the flow.
            // TODO if it becomes a UX issue: switch agents to a tool_use
            // declaration of artifacts/tasks instead of inline sentinels,
            // so the streaming text stays clean.
            // OpenAI shape: final chunk with finish_reason set, then an
            // optional usage-only chunk (empty choices[], usage populated)
            // when stream_options.include_usage was requested. We emit it
            // unconditionally so noqta.tn's voice forwarder always has
            // token counts to debit credits against — the OpenAI SDK and
            // ElevenLabs both ignore unknown chunks gracefully.
            res.write(`data: ${JSON.stringify({ ...baseChunk, choices: [{ index: 0, delta: {}, finish_reason: "stop" }] })}\n\n`)
            if (resp.usage) {
              const usageChunk = {
                ...baseChunk,
                choices: [],
                usage: {
                  prompt_tokens: resp.usage.inputTokens || 0,
                  completion_tokens: resp.usage.outputTokens || 0,
                  total_tokens: (resp.usage.inputTokens || 0) + (resp.usage.outputTokens || 0),
                },
              }
              res.write(`data: ${JSON.stringify(usageChunk)}\n\n`)
            }
            res.write("data: [DONE]\n\n")
            res.end()
          } catch (e: any) {
            try {
              send({ content: `\n[error] ${(e as Error).message}` }, "stop")
              res.write("data: [DONE]\n\n")
            } catch { /* socket may already be dead */ }
            res.end()
          }
          return
        }

        try {
          const resp = await this.registry.execute({
            agentId,
            message,
            context: { channel: "web-chat", sender: userId, chatId: conversationId },
            systemPromptAppend: chatSystemAppend,
          })

          if (resp.error) {
            this.json(res, 502, { error: resp.error }); return
          }

          let reply = resp.content ?? ""
          let taskPayload: { summary: string } | undefined
          const artifacts: Array<{ type: string; filename: string; mime: string }> = []

          // Strip and parse all <agentx-artifact> sentinels.
          const artifactRe = /<agentx-artifact>([\s\S]*?)<\/agentx-artifact>/gi
          reply = reply.replace(artifactRe, (_full, inner) => {
            try {
              const parsed = JSON.parse(inner.trim())
              if (parsed && typeof parsed.filename === "string" && typeof parsed.mime === "string") {
                const mime: string = parsed.mime
                const type = mime.startsWith("image/") ? "image"
                  : mime === "application/pdf" ? "pdf"
                  : mime.startsWith("text/") ? "text"
                  : "file"
                artifacts.push({ type, filename: parsed.filename, mime })
              }
            } catch { /* malformed — ignore */ }
            return ""
          }).trim()

          // Parse <agentx-task>{"summary":"…"}</agentx-task> sentinel from reply.
          const sentinelRe = /<agentx-task>([\s\S]*?)<\/agentx-task>/i
          const match = sentinelRe.exec(reply)
          if (match) {
            try {
              const parsed = JSON.parse(match[1].trim())
              if (parsed && typeof parsed.summary === "string") {
                taskPayload = { summary: parsed.summary }
              }
            } catch { /* malformed — ignore */ }
            reply = reply.replace(match[0], "").trim()
          }

          let queuedTask: { id: string; status: string; position: number | null } | undefined
          if (taskPayload && this.db) {
            const taskId = newEventId().slice(0, 12) // short enough for inline display
            const maxConcurrent = parseInt(process.env.MAX_CONCURRENT_CHAT_TASKS || "2", 10)
            const result = insertTaskQueue(this.db, {
              id: taskId,
              conversationId,
              agentId,
              summary: taskPayload.summary,
            }, maxConcurrent)
            queuedTask = { id: result.id, status: result.status, position: result.position }
          }

          const out: Record<string, unknown> = { reply }
          if (resp.usage) out.tokens = { input: resp.usage.inputTokens, output: resp.usage.outputTokens }
          if (taskPayload) out.task = taskPayload
          if (queuedTask) out.queued_task = queuedTask
          if (artifacts.length > 0) out.artifacts = artifacts

          this.json(res, 200, out)
        } catch (e: any) {
          this.json(res, 500, { error: "agent execute failed", message: (e as Error).message })
        }
        return
      }

      // GET /chat/task-queue/:id — task queue entry status
      if (req.method === "GET" && path.startsWith("/chat/task-queue/") && this.db) {
        const taskId = decodeURIComponent(path.replace(/^\/chat\/task-queue\//, ""))
        if (!taskId) { this.json(res, 400, { error: "task id required" }); return }
        const entry = getTaskQueue(this.db, taskId)
        if (!entry) { this.json(res, 404, { error: "not found" }); return }
        this.json(res, 200, entry)
        return
      }

      // GET /chat/task-queue?conversation_id=… — list tasks for a conversation
      if (req.method === "GET" && path === "/chat/task-queue" && this.db) {
        const urlObj = new URL(req.url || "/", `http://${req.headers.host || "_"}`)
        const convId = urlObj.searchParams.get("conversation_id") || ""
        if (!convId) { this.json(res, 400, { error: "conversation_id query param required" }); return }
        const entries = listTaskQueueByConversation(this.db, convId)
        this.json(res, 200, { tasks: entries })
        return
      }

      // PATCH /chat/task-queue/:id — mark a task done/error
      if (req.method === "PATCH" && path.startsWith("/chat/task-queue/") && this.db) {
        const taskId = decodeURIComponent(path.replace(/^\/chat\/task-queue\//, ""))
        let body: any
        try { body = await readJsonBody(req) } catch { body = {} }
        const status = body?.status === "error" ? "error" : "done"
        completeTaskQueue(this.db, taskId, status)
        const updated = getTaskQueue(this.db, taskId)
        this.json(res, updated ? 200 : 404, updated ?? { error: "not found" })
        return
      }

      // GET /agents/:id/workspace/*filepath — binary passthrough for agent workspace files.
      //
      // Authorization: Bearer <AGENTX_CHAT_SECRET>
      //
      // Returns the raw file with an appropriate Content-Type so browsers /
      // noqta.tn can display images, PDFs, and other artifacts the agent wrote
      // during a /chat turn. Path traversal is blocked — the resolved path must
      // stay inside the agent's workspace directory.
      const workspaceFileMatch = req.method === "GET" && path.match(/^\/agents\/([^/]+)\/workspace\/(.+)$/)
      if (workspaceFileMatch) {
        const chatSecret = process.env.AGENTX_CHAT_SECRET || process.env.TELEGRAM_BOT_API_SECRET
        if (chatSecret) {
          const authHeader = req.headers["authorization"] || ""
          const token = authHeader.startsWith("Bearer ") ? authHeader.slice(7).trim() : ""
          if (token !== chatSecret) {
            this.json(res, 401, { error: "unauthorized" }); return
          }
        }
        const wsAgentId = workspaceFileMatch[1]
        const rawFilePath = decodeURIComponent(workspaceFileMatch[2])
        const agentDef = this.registry.getAgent(wsAgentId)
        if (!agentDef) { this.json(res, 404, { error: `agent "${wsAgentId}" not found` }); return }

        // Resolve and guard against path traversal.
        const workspaceDir = resolve(process.cwd(), agentDef.workspace)
        const resolvedFile = resolve(workspaceDir, rawFilePath)
        if (!resolvedFile.startsWith(workspaceDir + sep) && resolvedFile !== workspaceDir) {
          this.json(res, 403, { error: "path traversal denied" }); return
        }
        if (!existsSync(resolvedFile)) {
          this.json(res, 404, { error: "file not found" }); return
        }

        // Derive Content-Type from extension.
        const ext = extname(resolvedFile).toLowerCase()
        const mimeMap: Record<string, string> = {
          ".png": "image/png", ".jpg": "image/jpeg", ".jpeg": "image/jpeg",
          ".gif": "image/gif", ".webp": "image/webp", ".svg": "image/svg+xml",
          ".pdf": "application/pdf",
          ".csv": "text/csv", ".txt": "text/plain", ".md": "text/markdown",
          ".json": "application/json", ".html": "text/html",
          ".mp4": "video/mp4", ".webm": "video/webm",
        }
        const contentType = mimeMap[ext] ?? "application/octet-stream"
        res.writeHead(200, {
          "Content-Type": contentType,
          "Cache-Control": "private, max-age=3600",
          "Access-Control-Allow-Origin": "*",
        })
        createReadStream(resolvedFile).pipe(res)
        return
      }

      // POST /api/workflows/signal/:name — manual signal emission.
      if (req.method === "POST" && path.startsWith("/api/workflows/signal/") && this.workflowDispatcher) {
        const name = decodeURIComponent(path.replace(/^\/api\/workflows\/signal\//, ""))
        if (!name) { this.json(res, 400, { error: "signal name required" }); return }
        let body: any
        try { body = await readJsonBody(req) } catch (e: any) {
          this.json(res, 400, { error: "invalid JSON body", message: e.message }); return
        }
        const emission = this.workflowDispatcher.emitSignal({
          name,
          scope: body?.scope,
          workflowId: body?.workflowId,
          payload: body?.payload,
        })
        this.json(res, 200, { ok: true, emission })
        return
      }
      // /processes — composition-tree + SLA view of runs.
      if (req.method === "GET" && path === "/processes") {
        const { renderProcessesPage } = await import("./ui/pages/processes")
        res.writeHead(200, { "Content-Type": "text/html; charset=utf-8" })
        res.end(renderProcessesPage({}))
        return
      }
      // Static browser call page.
      if (req.method === "GET" && path === "/call") {
        this.serveCallPage(res)
        return
      }

      // Dynamic routes (before static switch)
      if (req.method === "POST" && path.startsWith("/webhook/")) {
        // GitHub channel adapter: intercept webhooks with X-GitHub-Event header
        // when the GitHub channel is enabled — routes internally by repo.
        if (this.github && req.headers["x-github-event"]) {
          const body = await new Promise<string>((resolve) => {
            let data = ""
            req.on("data", (chunk: Buffer) => (data += chunk.toString()))
            req.on("end", () => resolve(data))
            req.on("error", () => resolve(""))
          })
          let parsed: Record<string, unknown>
          try {
            // GitHub may send as application/json or application/x-www-form-urlencoded
            const contentType = req.headers["content-type"] || ""
            if (contentType.includes("form-urlencoded") && body.startsWith("payload=")) {
              parsed = JSON.parse(decodeURIComponent(body.slice(8)))
            } else {
              parsed = body ? JSON.parse(body) : {}
            }
          } catch { parsed = {} }
          this.log(`[github] webhook body keys: ${Object.keys(parsed).slice(0, 5).join(", ")} | repo: ${(parsed.repository as any)?.full_name || "MISSING"}`)
          // Respond immediately (GitHub has a 10s timeout)
          res.writeHead(202, { "Content-Type": "application/json" })
          res.end(JSON.stringify({ ok: true, channel: "github", status: "accepted" }))
          // Process asynchronously
          this.github.handleWebhook(
            req.headers as Record<string, string | string[] | undefined>,
            parsed,
            body,  // raw body for signature verification
          ).catch(e => this.log(`[github] webhook handler error: ${(e as Error).message}`))
          return
        }
        await this.webhooks.handle(req, res, path)
        return
      }

      // SSE stream for a single running task — drives the dashboard modal.
      // GET /agents/:agentId/tasks/:taskId/stream
      const taskStreamMatch = req.method === "GET" && path.match(/^\/agents\/([^/]+)\/tasks\/([^/]+)\/stream$/)
      if (taskStreamMatch) {
        this.handleTaskStream(req, res, taskStreamMatch[1], taskStreamMatch[2])
        return
      }

      // Persisted task history (for the dashboard "Recent activities" panel).
      // GET /agents/:agentId/tasks?limit=N  → list of summaries (newest first)
      // GET /agents/:agentId/tasks/:taskId  → one full record (transcript + response)
      const taskHistoryListMatch = req.method === "GET" && path.match(/^\/agents\/([^/]+)\/tasks$/)
      if (taskHistoryListMatch) {
        const limit = Math.max(1, Math.min(500, parseInt(url.searchParams.get("limit") || "50", 10) || 50))
        this.json(res, 200, this.registry.listTaskHistory(taskHistoryListMatch[1], limit))
        return
      }
      const taskRecordMatch = req.method === "GET" && path.match(/^\/agents\/([^/]+)\/tasks\/([^/]+)$/)
      if (taskRecordMatch) {
        const rec = this.registry.getTaskRecord(taskRecordMatch[1], taskRecordMatch[2])
        if (!rec) { this.json(res, 404, { error: "not found" }); return }
        this.json(res, 200, rec)
        return
      }

      // Resolved agent config (improvement plan #4). Operators can ask
      // "what permissions does my agent actually run with" without
      // reading runtime source. Returns: tier, model, permissionMode,
      // resolved spawn-time skip-permissions flag, persistentProcess,
      // toolUseRequired, workspace, and the mention list. Excludes
      // systemPrompt (large, dump via /admin if needed).
      const agentRecordMatch = req.method === "GET" && path.match(/^\/agents\/([^/]+)$/)
      if (agentRecordMatch) {
        const id = agentRecordMatch[1]
        const def = this.config.agents[id]
        if (!def) { this.json(res, 404, { error: "agent not found" }); return }
        const perm = resolvePermission(def)
        this.json(res, 200, {
          id,
          name: def.name,
          tier: def.tier,
          model: def.model ?? null,
          workspace: def.workspace,
          mentions: def.mentions ?? [],
          maxConcurrent: def.maxConcurrent,
          maxExecutionMinutes: def.maxExecutionMinutes,
          permission: perm,
          persistentProcess: !!(def as any).persistentProcess,
          toolUseRequired: (def as any).toolUseRequired ?? [],
        })
        return
      }

      // Per-task execution traces (improvement plan #2). Cross-agent ULID
      // keyed; populated by capture sites in registry.execute / runtime.ts.
      // Returns 503 when SQLite is unavailable so callers can degrade
      // gracefully rather than seeing 500s.
      //   GET /traces?agentId=&channel=&chatId=&workflowRunId=&status=
      //              &since=<msEpoch>&until=<msEpoch>&limit=N
      //   GET /traces/:taskId
      const traceListMatch = req.method === "GET" && path === "/traces"
      if (traceListMatch) {
        if (!this.db) { this.json(res, 503, { error: "sqlite not opened" }); return }
        const numParam = (k: string): number | undefined => {
          const v = url.searchParams.get(k)
          if (v === null || v === "") return undefined
          const n = parseInt(v, 10)
          return Number.isFinite(n) ? n : undefined
        }
        const traces = listTraces(this.db, {
          agentId: url.searchParams.get("agentId") || undefined,
          channel: url.searchParams.get("channel") || undefined,
          chatId: url.searchParams.get("chatId") || undefined,
          workflowRunId: url.searchParams.get("workflowRunId") || undefined,
          status: url.searchParams.get("status") || undefined,
          since: numParam("since"),
          until: numParam("until"),
          limit: numParam("limit"),
        })
        this.json(res, 200, { traces })
        return
      }
      const traceRecordMatch = req.method === "GET" && path.match(/^\/traces\/([^/]+)$/)
      if (traceRecordMatch) {
        if (!this.db) { this.json(res, 503, { error: "sqlite not opened" }); return }
        const rec = getTrace(this.db, traceRecordMatch[1])
        if (!rec) { this.json(res, 404, { error: "not found" }); return }
        this.json(res, 200, rec)
        return
      }

      // Per-agent self-test canary (handoff #6). Runs a minimal prompt
      // through the agent's full execution path — including any declared
      // toolUseRequired — and reports the result. Used by:
      //   - operators verifying a fresh deploy ("is the agent's API key
      //     working? does it actually fire its required tools?")
      //   - CI / boot-validation scripts (curl + jq, no daemon coupling)
      //   - the dashboard's per-agent health badge (future)
      // Always uses freshSession + a unique chatId so the canary never
      // pollutes a live chat session.
      const selftestMatch = req.method === "POST" && path.match(/^\/agents\/([^/]+)\/selftest$/)
      if (selftestMatch) {
        const agentId = selftestMatch[1]
        const body = await readBody(req).catch(() => ({} as Record<string, unknown>))
        const probeMsg = (body as Record<string, unknown>).message
        const probe = typeof probeMsg === "string" && probeMsg.trim()
          ? probeMsg
          : "Reply with the single word: OK"
        const start = Date.now()
        const response = await this.registry.execute({
          agentId,
          message: probe,
          freshSession: true,
          context: {
            channel: "selftest",
            chatId: `selftest:${start.toString(36)}`,
            sender: "selftest",
          },
        }, () => {})
        const durationMs = Date.now() - start
        if (response.error) {
          this.json(res, 200, {
            ok: false,
            agentId,
            error: response.error,
            errorKind: response.errorKind,
            durationMs,
            usage: response.usage,
            tokens: response.usage ? { in: response.usage.inputTokens, out: response.usage.outputTokens } : undefined,
          })
          return
        }
        this.json(res, 200, {
          ok: true,
          agentId,
          content: response.content,
          durationMs,
          usage: response.usage,
          tokens: response.usage ? { in: response.usage.inputTokens, out: response.usage.outputTokens } : undefined,
          billedModel: response.billedModel,
        })
        return
      }

      // Persistent-process registry — operator surface (improvement plan #5,
      // persistent flavor). Returns 503 when no agent has persistentProcess
      // enabled (registry singleton not initialized). The path lives under
      // /api/processes so it doesn't collide with the existing /processes
      // HTML dashboard route registered earlier in this dispatcher.
      //   GET  /api/processes
      //   POST /api/processes/kill   { agentId, channel, chatId, reason? }
      if (req.method === "GET" && path === "/api/processes") {
        if (!this.processRegistry) { this.json(res, 503, { error: "process registry not enabled" }); return }
        this.json(res, 200, { processes: this.processRegistry.list() })
        return
      }
      // Built-in actions (improvement plan #6).
      //   GET  /api/actions/builtin              — list registered built-ins
      //   POST /api/actions/builtin/:name        — run one with body as input
      if (req.method === "GET" && path === "/api/actions/builtin") {
        this.json(res, 200, { actions: listBuiltins() })
        return
      }
      const builtinRunMatch = req.method === "POST" && path.match(/^\/api\/actions\/builtin\/([a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*)$/)
      if (builtinRunMatch) {
        const name = builtinRunMatch[1]
        if (!getBuiltin(name)) { this.json(res, 404, { error: `unknown built-in: ${name}` }); return }
        try {
          const body = await readJsonBody(req)
          const output = await runBuiltin(name, body)
          this.json(res, 200, { output })
        } catch (e: any) {
          // Zod validation errors surface as 400; runtime errors as 500.
          // Distinguishing on `e.name === "ZodError"` keeps callers'
          // retry logic simple — 4xx = fix the input, 5xx = retry.
          if (e?.name === "ZodError") {
            this.json(res, 400, { error: e.message })
          } else {
            this.json(res, 500, { error: e?.message || String(e) })
          }
        }
        return
      }

      if (req.method === "POST" && path === "/api/processes/kill") {
        if (!this.processRegistry) { this.json(res, 503, { error: "process registry not enabled" }); return }
        try {
          const body = await readJsonBody(req)
          const { agentId, channel, chatId, reason } = body as { agentId: string; channel: string; chatId: string; reason?: string }
          if (!agentId || !channel || !chatId) {
            this.json(res, 400, { error: "agentId, channel, chatId required" })
            return
          }
          await this.processRegistry.kill({ agentId, channel, chatId }, reason || "operator")
          this.json(res, 200, { killed: { agentId, channel, chatId } })
        } catch (e: any) {
          this.json(res, 500, { error: e?.message || String(e) })
        }
        return
      }

      // Operator stop: abort the in-flight execution for a specific runningTask.id.
      //   POST /api/tasks/:taskId/cancel    body: { reason? }
      // 200 → { ok, agentId, channel, chatId }
      // 404 → task not running (already finished or never existed)
      const cancelMatch = req.method === "POST" && path.match(/^\/api\/tasks\/([^/]+)\/cancel$/)
      if (cancelMatch) {
        const taskId = decodeURIComponent(cancelMatch[1])
        try {
          const body = await readJsonBody(req).catch(() => ({}))
          const reason = typeof (body as any)?.reason === "string" ? (body as any).reason : "operator"
          const result = this.registry.cancelRunningTask(taskId, reason)
          if (!result) { this.json(res, 404, { error: `no running task with id ${taskId}` }); return }
          this.json(res, 200, { ok: true, cancelled: { taskId, ...result, reason } })
        } catch (e: any) {
          this.json(res, 500, { error: e?.message || String(e) })
        }
        return
      }

      // Operator follow-up: enqueue a correction/update message for an in-flight
      // task. With replace=true the current run is aborted so the new message
      // runs immediately; otherwise it waits for the current run to finish.
      //   POST /api/tasks/:taskId/followup  body: { message, replace?, sender? }
      // 200 → { ok, agentId, channel, chatId, replaced, pending }
      // 404 → task not running
      const followupMatch = req.method === "POST" && path.match(/^\/api\/tasks\/([^/]+)\/followup$/)
      if (followupMatch) {
        const taskId = decodeURIComponent(followupMatch[1])
        try {
          const body = await readJsonBody(req)
          const message = typeof (body as any)?.message === "string" ? (body as any).message.trim() : ""
          if (!message) { this.json(res, 400, { error: "message required" }); return }
          const replace = (body as any)?.replace === true
          const sender = typeof (body as any)?.sender === "string" && (body as any).sender.trim()
            ? (body as any).sender.trim()
            : "operator"
          const result = this.registry.queueFollowUp(taskId, message, sender, { replace })
          if (!result) { this.json(res, 404, { error: `no running task with id ${taskId}` }); return }
          this.json(res, 200, { ok: true, taskId, ...result })
        } catch (e: any) {
          this.json(res, 500, { error: e?.message || String(e) })
        }
        return
      }

      // OpenAI-compatible endpoint for ElevenLabs, Cursor, etc.
      // POST /v1/chat/completions or /llm/:agentId/v1/chat/completions
      if (req.method === "POST" && (path === "/v1/chat/completions" || path.match(/^\/llm\/[^/]+\/v1\/chat\/completions$/))) {
        await this.handleOpenAICompat(req, res, path)
        return
      }

      // Business layer endpoints (/business/status, /business/work, ...)
      if (this.business && path.startsWith("/business")) {
        const handled = await this.business.handleHttp(`${req.method} ${path}`, req, res)
        if (handled) return
      }

      switch (`${req.method} ${path}`) {
        case "GET /health": {
          const ruleHealth = this.projectRules.health()
          this.json(res, 200, {
            status: "ok",
            node: this.config.node,
            uptime: process.uptime(),
            agents: this.registry.list(),
            crons: this.cron.list().map((j) => ({ id: j.id, enabled: j.enabled, nextRun: j.nextRun })),
            mesh: this.mesh?.directory() || [],
            projectRules: { count: ruleHealth.count, errors: ruleHealth.errors },
            usage: this.resolveTodayUsage(),
          })
          break
        }

        case "GET /usage":
          this.json(res, 200, this.registry.getUsage(7))
          break

        case "GET /agents":
          this.json(res, 200, this.registry.list().map(agent => ({
            ...agent,
            skillCount: listAgentFiles(agent.workspace).skills.length,
          })))
          break

        case "GET /api/admin/projects": {
          // Aggregated per-project view: rule files + workflows + channel
          // routes + contacts. Powers the /admin/projects dashboard page.
          const { computeProjectsAggregate } = await import("./projects-api")
          if (!this.workflowStore) {
            this.json(res, 503, { error: "workflow store not initialized" })
            break
          }
          const result = computeProjectsAggregate({
            config: this.config,
            rules: this.projectRules,
            workflowStore: this.workflowStore,
            cwd: process.cwd(),
          })
          this.json(res, 200, result)
          break
        }

        case "POST /api/admin/projects/workflows/link":
        case "POST /api/admin/projects/workflows/unlink": {
          // Tag (link) or strip (unlink) a workflow's `project:` field
          // so it shows up under a project on /admin/projects without
          // hand-editing the YAML. The mutation rewrites the workflow
          // file in place — preserves comments, formatting, ordering.
          // workflowStore.list() reads fresh each call so no reload is
          // needed.
          const body = await readBody(req)
          const link = req.url?.endsWith("/link")
          const projectKey = String(body.projectKey || "").trim()
          const workflowId = String(body.workflowId || "").trim()
          if (!projectKey || !workflowId) {
            this.json(res, 400, { error: "projectKey + workflowId required" })
            break
          }
          if (!/^[a-z0-9][a-z0-9_-]*$/.test(workflowId)) {
            this.json(res, 400, { error: "invalid workflow id" })
            break
          }
          const { setWorkflowProject } = await import("./projects-mutate")
          try {
            const result = setWorkflowProject({
              workflowId,
              projectKey: link ? projectKey : null,
              cwd: process.cwd(),
            })
            this.json(res, 200, result)
          } catch (e: any) {
            this.json(res, 400, { error: e?.message || "mutation failed" })
          }
          break
        }

        case "POST /api/admin/projects/header":
        case "POST /api/admin/projects/clauses":
        case "POST /api/admin/projects/contacts/link":
        case "POST /api/admin/projects/contacts/unlink":
        case "POST /api/admin/projects/create":
        case "POST /api/admin/projects/delete": {
          // Project rule mutations — header (PATCH-style partial),
          // contacts list, create, delete. Body always carries
          // projectKey; specific endpoints add their own fields.
          const body = await readBody(req)
          const projectKey = String(body.projectKey || "").trim()
          if (!projectKey) {
            this.json(res, 400, { error: "projectKey required" })
            break
          }
          if (!/^[a-zA-Z0-9._-]+(\/[a-zA-Z0-9._-]+)*$/.test(projectKey)) {
            this.json(res, 400, { error: "invalid projectKey" })
            break
          }
          const mut = await import("./projects-mutate")
          try {
            const cwd = process.cwd()
            const url = req.url || ""
            if (url.endsWith("/header")) {
              this.json(res, 200, mut.patchProjectHeader({ projectKey, cwd, patch: body.patch || {} }))
            } else if (url.endsWith("/clauses")) {
              // body.gitlab / body.github come in as untyped JSON. Trust
              // the form shape — the mutator validates by re-parsing the
              // resulting YAML before write. Cast through unknown so
              // TypeScript doesn't reject the boundary read.
              this.json(res, 200, mut.setProjectClauses({
                projectKey, cwd,
                gitlab: body.gitlab as unknown as undefined,
                github: body.github as unknown as undefined,
              }))
            } else if (url.endsWith("/contacts/link")) {
              const cid = String(body.contactId || "").trim()
              if (!cid) { this.json(res, 400, { error: "contactId required" }); break }
              this.json(res, 200, mut.linkContact({ projectKey, cwd, contactId: cid }))
            } else if (url.endsWith("/contacts/unlink")) {
              const cid = String(body.contactId || "").trim()
              if (!cid) { this.json(res, 400, { error: "contactId required" }); break }
              this.json(res, 200, mut.unlinkContact({ projectKey, cwd, contactId: cid }))
            } else if (url.endsWith("/create")) {
              const kind = String(body.kind || "other") as any
              this.json(res, 200, mut.createProjectRule({
                projectKey, cwd, kind,
                displayName: typeof body.displayName === "string" ? body.displayName : undefined,
                homeUrl: typeof body.homeUrl === "string" ? body.homeUrl : undefined,
                runbook: typeof body.runbook === "string" ? body.runbook : undefined,
                agent: typeof body.agent === "string" ? body.agent : undefined,
              }))
            } else if (url.endsWith("/delete")) {
              this.json(res, 200, mut.deleteProjectRule({ projectKey, cwd }))
            }
          } catch (e: any) {
            this.json(res, 400, { error: e?.message || "mutation failed" })
          }
          break
        }

        case "GET /whatsapp/state": {
          const { getWhatsAppState } = await import("./whatsapp-state")
          this.json(res, 200, getWhatsAppState())
          break
        }

        case "GET /whatsapp/qr.svg": {
          const { getWhatsAppState } = await import("./whatsapp-state")
          const s = getWhatsAppState()
          if (!s.qr) { res.writeHead(204, { "Cache-Control": "no-store" }); res.end(); break }
          try {
            // @ts-ignore — qrcode has no shipped types
            const mod = await import("qrcode")
            const QRCode: any = (mod as any).default || mod
            const svg: string = await QRCode.toString(s.qr, { type: "svg", errorCorrectionLevel: "L", margin: 1, color: { dark: "#000", light: "#fff" } })
            res.writeHead(200, { "Content-Type": "image/svg+xml; charset=utf-8", "Cache-Control": "no-store" })
            res.end(svg)
          } catch (e: any) {
            res.writeHead(503, { "Content-Type": "text/plain; charset=utf-8" })
            res.end("qrcode package not installed or failed: " + (e?.message || "unknown"))
          }
          break
        }

        case "GET /whatsapp/chats": {
          const wa = this.router.getChannel("whatsapp") as WhatsAppAdapter | undefined
          if (!wa) { this.json(res, 503, { error: "WhatsApp channel not enabled" }); break }
          this.json(res, 200, { chats: wa.listChats() })
          break
        }

        case "GET /whatsapp/contacts": {
          const wa = this.router.getChannel("whatsapp") as WhatsAppAdapter | undefined
          if (!wa) { this.json(res, 503, { error: "WhatsApp channel not enabled" }); break }
          this.json(res, 200, { contacts: wa.listContacts() })
          break
        }

        case "POST /whatsapp/ingest": {
          const wa = this.router.getChannel("whatsapp") as WhatsAppAdapter | undefined
          if (!wa) { this.json(res, 503, { error: "WhatsApp channel not enabled" }); break }
          const body = await readBody(req)
          const cfg = this.config.channels.whatsapp.ingest
          if (!cfg.enabled && !body.force) {
            this.json(res, 400, { error: "channels.whatsapp.ingest.enabled is false. Set it to true in agentx.json or pass {\"force\": true}." })
            break
          }
          const agentId = (typeof body.agent === "string" && body.agent)
            || this.config.channels.whatsapp.defaultAgent
            || this.config.node.defaultAgent
          if (!agentId) {
            this.json(res, 400, { error: "No agent to own the entries. Pass {agent: '...'} or set channels.whatsapp.defaultAgent." })
            break
          }
          const dryRun = !!body.dryRun
          const { runSweep } = await import("@/wiki/ingest-whatsapp")
          const store = this.registry.getWikiHub().getAgentWiki(agentId)
          // Support per-chat CLI commands (ingest-contact / ingest-chat):
          // narrow the allowlist to the single JID and optionally override
          // the ingest mode for this pass only. Leaves the persistent
          // allowlist in agentx.json untouched.
          let effectiveCfg = cfg
          if (typeof body.onlyJid === "string") {
            const jid = body.onlyJid as string
            const isGroup = jid.endsWith("@g.us")
            const forced: "metadata-only" | "messages" | undefined =
              body.forceMode === "metadata-only" || body.forceMode === "messages"
                ? body.forceMode
                : undefined
            effectiveCfg = {
              ...cfg,
              // Ignore the master enabled flag when a specific JID was named
              // via the CLI — operator has explicitly pointed at this one.
              enabled: true,
              mode: forced ?? cfg.mode,
              // Narrow scope to exactly this JID regardless of existing lists.
              allowContacts: isGroup || body.onlyKind === "group" ? [] : [jid],
              allowGroups: isGroup || body.onlyKind === "group" ? [jid] : [],
              denyContacts: [],
              denyGroups: [],
            }
          }
          const report = await runSweep({
            source: wa,
            store,
            config: effectiveCfg,
            agentId,
            dryRun,
          })
          this.json(res, 200, report)
          break
        }

        case "GET /crons":
          this.json(res, 200, this.cron.list())
          break

        case "GET /crons/health":
          this.json(res, 200, this.cron.health())
          break

        case "GET /crons/runs": {
          const date = url.searchParams.get("date") || new Date().toISOString().slice(0, 10)
          const timezone = url.searchParams.get("timezone") || "UTC"
          const jobId = url.searchParams.get("jobId") || undefined
          const runs = await readCronRunHistory({ date, timezone, jobId })
          this.json(res, 200, { date, timezone, runs })
          break
        }

        case "GET /mesh":
          this.json(res, 200, this.mesh?.directory() || [])
          break

        case "GET /debug": {
          const { getDebugState, getDebugLogs } = await import("@/observability/debug")
          this.json(res, 200, { ...getDebugState(), recentLogs: getDebugLogs(50) })
          break
        }

        case "POST /debug/on": {
          const { setDebug } = await import("@/observability/debug")
          const cats = url.searchParams.get("categories")?.split(",") || ["all"]
          setDebug(true, cats as any)
          this.log(`Debug enabled: ${cats.join(", ")}`)
          this.json(res, 200, { enabled: true, categories: cats })
          break
        }

        case "POST /debug/off": {
          const { setDebug } = await import("@/observability/debug")
          setDebug(false)
          this.log("Debug disabled")
          this.json(res, 200, { enabled: false })
          break
        }

        case "POST /gitlab/react": {
          // Forwarded from a mesh peer: perform a 👀 reaction using local agent token
          const body = await readBody(req)
          const { project, noteableType, noteableIid, noteId, agentId } = body as any
          const resolved = this.resolveGitlabTokenForAgent(agentId)
          const token = resolved.token
          const host = this.config.channels.gitlab?.host
          this.log(`[gitlab/react] agent="${agentId}" project="${project}" source=${resolved.source} host=${host || "MISSING"}`)
          if (!token || !host) {
            this.json(res, 404, {
              error: "no gitlab token for agent",
              debug: { agentId, source: resolved.source, hasGlobalToken: !!this.config.channels.gitlab?.token, hasHost: !!host },
            })
            break
          }
          const encoded = encodeURIComponent(project)
          const ep = noteableType === "issue"
            ? `${host}/api/v4/projects/${encoded}/issues/${noteableIid}/notes/${noteId}/award_emoji`
            : `${host}/api/v4/projects/${encoded}/merge_requests/${noteableIid}/notes/${noteId}/award_emoji`
          try {
            const glRes = await fetch(ep, {
              method: "POST",
              headers: { "Content-Type": "application/json", "PRIVATE-TOKEN": token },
              body: JSON.stringify({ name: "eyes" }),
            })
            const respBody = await glRes.text().catch(() => "")
            this.log(`[gitlab/react] -> POST ${ep} : ${glRes.status} ${respBody.slice(0, 120)}`)
            this.json(res, 200, { ok: glRes.ok, status: glRes.status, gitlabResponse: respBody.slice(0, 200) })
          } catch (e: any) {
            this.log(`[gitlab/react] FETCH ERROR: ${e.message}`)
            this.json(res, 500, { error: e.message })
          }
          break
        }

        case "POST /gitlab/send-note": {
          // Forwarded from a mesh peer: post a note using local agent token
          // so the comment shows up under the agent's real GitLab user.
          const body = await readBody(req)
          const { project, noteableType, noteableIid, agentId, text } = body as any
          const resolved = this.resolveGitlabTokenForAgent(agentId)
          const token = resolved.token
          const host = this.config.channels.gitlab?.host
          if (!token || !host) {
            this.json(res, 404, { error: "no gitlab token for agent", debug: { agentId, source: resolved.source, hasToken: !!token, hasHost: !!host } })
            break
          }
          this.log(`[gitlab/send-note] agent="${agentId}" source=${resolved.source}`)
          const encoded = encodeURIComponent(project)
          const ep = noteableType === "issue"
            ? `${host}/api/v4/projects/${encoded}/issues/${noteableIid}/notes`
            : `${host}/api/v4/projects/${encoded}/merge_requests/${noteableIid}/notes`
          try {
            const glRes = await fetch(ep, {
              method: "POST",
              headers: { "Content-Type": "application/json", "PRIVATE-TOKEN": token },
              body: JSON.stringify({ body: text }),
            })
            const respBody = await glRes.text().catch(() => "")
            let noteId = ""
            try { noteId = String((JSON.parse(respBody) as any).id || "") } catch { /* ignore */ }
            this.log(`[gitlab/send-note] agent="${agentId}" -> POST ${ep} : ${glRes.status} noteId=${noteId || "?"}`)
            this.json(res, 200, { ok: glRes.ok, status: glRes.status, noteId })
          } catch (e: any) {
            this.log(`[gitlab/send-note] FETCH ERROR: ${e.message}`)
            this.json(res, 500, { error: e.message })
          }
          break
        }

        case "POST /gitlab/log-time": {
          // Forwarded from a mesh peer: log spent time under the agent's own user.
          const body = await readBody(req)
          const { project, noteableType, noteableIid, agentId, durationMs } = body as any
          const resolved = this.resolveGitlabTokenForAgent(agentId)
          const token = resolved.token
          const host = this.config.channels.gitlab?.host
          if (!token || !host) {
            this.json(res, 404, { error: "no gitlab token for agent", debug: { agentId, source: resolved.source } })
            break
          }
          const totalSeconds = Math.max(60, Math.round(Number(durationMs) / 1000))
          const hours = Math.floor(totalSeconds / 3600)
          const minutes = Math.ceil((totalSeconds % 3600) / 60)
          const duration = hours > 0 ? `${hours}h${minutes > 0 ? ` ${minutes}m` : ""}` : `${minutes}m`
          const encoded = encodeURIComponent(project)
          const seg = noteableType === "merge_request" ? "merge_requests" : "issues"
          const ep = `${host}/api/v4/projects/${encoded}/${seg}/${noteableIid}/add_spent_time`
          try {
            const glRes = await fetch(ep, {
              method: "POST",
              headers: { "Content-Type": "application/json", "PRIVATE-TOKEN": token },
              body: JSON.stringify({ duration }),
            })
            this.log(`[gitlab/log-time] agent="${agentId}" duration=${duration} -> POST ${ep} : ${glRes.status}`)
            this.json(res, 200, { ok: glRes.ok, status: glRes.status, duration })
          } catch (e: any) {
            this.log(`[gitlab/log-time] FETCH ERROR: ${e.message}`)
            this.json(res, 500, { error: e.message })
          }
          break
        }

        case "POST /github/send-comment": {
          // Forwarded from a mesh peer: post a comment using the local GitHub token.
          const body = await readBody(req)
          const { repo, issueNumber, agentId, text } = body as any
          // Resolve token: per-agent tokenFile, per-agent token, or global
          const ghConfig = this.config.channels.github
          const mappings = ghConfig?.agentMappings || []
          const mapping = mappings.find((m: any) => m.agentId === agentId)
          let token: string | undefined
          if (mapping?.tokenFile) {
            try { token = readFileSync(mapping.tokenFile, "utf-8").trim().split("\n")[0].trim() } catch { /* */ }
          }
          token = token || mapping?.token
          if (!token && ghConfig?.tokenFile) {
            try { token = readFileSync(ghConfig.tokenFile, "utf-8").trim().split("\n")[0].trim() } catch { /* */ }
          }
          token = token || ghConfig?.token
          if (!token) {
            this.json(res, 404, { error: "no github token for agent", debug: { agentId, hasMapping: !!mapping } })
            break
          }
          const ep = `https://api.github.com/repos/${repo}/issues/${issueNumber}/comments`
          try {
            const ghRes = await fetch(ep, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "User-Agent": "AgentX" },
              body: JSON.stringify({ body: text }),
            })
            const respBody = await ghRes.text().catch(() => "")
            let commentId = ""
            try { commentId = String((JSON.parse(respBody) as any).id || "") } catch { /* */ }
            this.log(`[github/send-comment] agent="${agentId}" -> POST ${ep} : ${ghRes.status} commentId=${commentId || "?"}`)
            this.json(res, 200, { ok: ghRes.ok, status: ghRes.status, commentId })
          } catch (e: any) {
            this.log(`[github/send-comment] FETCH ERROR: ${e.message}`)
            this.json(res, 500, { error: e.message })
          }
          break
        }

        case "POST /github/react": {
          // Forwarded from a mesh peer: react with 👀 on a GitHub comment.
          const body = await readBody(req)
          const { repo, commentId, agentId } = body as any
          const ghConfig = this.config.channels.github
          const mappings = ghConfig?.agentMappings || []
          const mapping = mappings.find((m: any) => m.agentId === agentId)
          let token: string | undefined
          if (mapping?.tokenFile) {
            try { token = readFileSync(mapping.tokenFile, "utf-8").trim().split("\n")[0].trim() } catch { /* */ }
          }
          token = token || mapping?.token
          if (!token && ghConfig?.tokenFile) {
            try { token = readFileSync(ghConfig.tokenFile, "utf-8").trim().split("\n")[0].trim() } catch { /* */ }
          }
          token = token || ghConfig?.token
          if (!token) {
            this.json(res, 404, { error: "no github token for agent" })
            break
          }
          const ep = `https://api.github.com/repos/${repo}/issues/comments/${commentId}/reactions`
          try {
            const ghRes = await fetch(ep, {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, "User-Agent": "AgentX" },
              body: JSON.stringify({ content: "eyes" }),
            })
            this.log(`[github/react] agent="${agentId}" -> POST ${ep} : ${ghRes.status}`)
            this.json(res, 200, { ok: ghRes.ok, status: ghRes.status })
          } catch (e: any) {
            this.log(`[github/react] FETCH ERROR: ${e.message}`)
            this.json(res, 500, { error: e.message })
          }
          break
        }

        case "POST /reload": {
          try {
            const result = await this.reload()
            this.json(res, 200, { ok: true, ...result })
          } catch (e: any) {
            this.json(res, 500, { ok: false, error: e?.message || String(e) })
          }
          break
        }

        case "POST /recall": {
          // Conversation recall — reads stored sessions for a given agent
          // (optionally narrowed to a channel/chatId) and returns turns
          // newest-first with a cursor for pagination. Read-only.
          const body = await readBody(req)
          if (!body.agent) {
            this.json(res, 400, { error: "Required: agent (id of the agent whose sessions to recall)" })
            break
          }
          try {
            const result = this.registry.getSessionStore().recallTurns({
              agentId: String(body.agent),
              channel: body.channel ? String(body.channel) : undefined,
              chatId: body.chatId ? String(body.chatId) : undefined,
              before: body.before ? String(body.before) : undefined,
              after: body.after ? String(body.after) : undefined,
              lookbackDays: typeof body.lookbackDays === "number" ? body.lookbackDays : undefined,
              limit: typeof body.limit === "number" ? body.limit : undefined,
              query: body.query ? String(body.query) : undefined,
              participants: Array.isArray(body.participants)
                ? body.participants.map((p: unknown) => String(p))
                : undefined,
            })
            this.json(res, 200, result)
          } catch (e: any) {
            this.json(res, 500, { error: e?.message || String(e) })
          }
          break
        }

        case "POST /chat/recent": {
          // Cross-agent view of a chat. Returns the most recent messages
          // across EVERY agent's session for the given (channel, chatId)
          // — sorted oldest-first so the caller reads it like a transcript.
          // Use cases:
          //   - Agent introspection ("what was just said in this chat?")
          //   - Multi-agent groups where one agent needs to see what another
          //     replied without going through A2A indirection (today's
          //     cx→devops→marketing speculation thread is the failure mode
          //     this fixes).
          // Bounded by sinceISO (default: last 24h) and limit (default: 30,
          // max 200) so a long-running group can't blow up an agent's prompt.
          const body = await readBody(req)
          if (!body.channel || !body.chatId) {
            this.json(res, 400, { error: "Required: channel, chatId" })
            break
          }
          try {
            const messages = this.registry.getSessionStore().recentByChatId({
              channel: String(body.channel),
              chatId: String(body.chatId),
              sinceISO: typeof body.sinceISO === "string" ? body.sinceISO : undefined,
              limit: typeof body.limit === "number" ? body.limit : undefined,
            })
            this.json(res, 200, { messages, count: messages.length })
          } catch (e: any) {
            this.json(res, 500, { error: e?.message || String(e) })
          }
          break
        }

        case "POST /send/agent": {
          // Explicit A2A send: route to another agent via mesh (or local
          // dispatch when the target lives on this daemon). This is the
          // deterministic path for "agent A asks agent B to do X" — no
          // contact-directory fallback, no name fuzzy-matching. Caller
          // must pass an exact agentId. Returns the resulting task id /
          // remote message id when the mesh accepts the task.
          const body = await readBody(req)
          if (!body.agentId || !body.text) {
            this.json(res, 400, { error: "Required: agentId, text" })
            break
          }
          const targetAgent = String(body.agentId)
          const text = String(body.text)
          // Local agent? Dispatch directly through the registry.
          const localDef = this.registry.getAgent(targetAgent)
          if (localDef) {
            try {
              const senderAgentId = body.senderAgentId ? String(body.senderAgentId) : undefined
              const response = await this.registry.execute({
                agentId: targetAgent,
                message: text,
                context: { channel: "a2a", sender: senderAgentId ? `agent:${senderAgentId}` : "agent", chatId: senderAgentId || "a2a" },
              })
              this.json(res, response.error ? 500 : 200, { ok: !response.error, content: response.content, error: response.error })
            } catch (e: any) {
              this.json(res, 500, { error: e.message })
            }
            break
          }
          // Remote agent? Find the mesh peer that advertises this agent.
          if (!this.mesh) {
            this.json(res, 400, { error: `Unknown agent "${targetAgent}" — mesh disabled, no remote lookup possible` })
            break
          }
          const directory = this.mesh.directory()
          const peer = directory.find((p) => p.healthy && p.skills.some((s) => s.id === targetAgent))
          if (!peer) {
            const known = [
              ...Object.keys(this.config.agents),
              ...directory.flatMap((p) => p.skills.map((s) => s.id)),
            ]
            this.json(res, 404, { error: `Unknown agent "${targetAgent}"`, known })
            break
          }
          try {
            const senderAgentId = body.senderAgentId ? String(body.senderAgentId) : undefined
            const messageId = await this.mesh.sendTask(peer.peer, text, targetAgent, { senderAgentId })
            this.json(res, 200, { ok: true, messageId, peer: peer.peer })
          } catch (e: any) {
            this.json(res, 500, { error: e.message, peer: peer.peer })
          }
          break
        }

        case "POST /send/contact": {
          // Resolve a free-form contact name through the contact directory,
          // pick a channel address, then dispatch via the regular
          // sendOutbound path. Refuses on miss/ambiguous/fuzzy(no-confirm)
          // so the caller can ask the user to disambiguate. Distinct from
          // /send (which requires a chatId) and /send/agent (which uses
          // mesh), so route_traces records why each path was taken.
          const body = await readBody(req)
          if (!body.contactName || !body.text) {
            this.json(res, 400, { error: "Required: contactName, text" })
            break
          }
          const name = String(body.contactName)
          const text = String(body.text)
          const preferredChannel = body.channel ? String(body.channel) : undefined
          const confirmed = body.confirmed === true
          const result = this.contacts.resolve(name)

          if (result.kind === "miss") {
            this.json(res, 404, { error: `No contact matches "${name}"`, hint: "Add the contact to .agentx/contacts.json and POST /reload." })
            break
          }
          if (result.kind === "ambiguous") {
            this.json(res, 409, { error: "ambiguous", candidates: result.candidates.map((c) => ({ id: c.id, name: c.name, channels: Object.keys(c.channels) })) })
            break
          }
          if (result.kind === "fuzzy" && !confirmed) {
            // Refuse silent fuzzy match — caller (the LLM) must confirm.
            // Returns the candidate so the agent can ask the user "did you mean X?".
            this.json(res, 409, {
              error: "fuzzy-match-needs-confirmation",
              candidate: { id: result.contact.id, name: result.contact.name, channels: Object.keys(result.contact.channels) },
              confidence: result.confidence,
              hint: "Re-call with confirmed:true once the user has confirmed this is the intended recipient.",
            })
            break
          }
          // Cross-channel collision check: if the name also resolves to a
          // registered local agent, surface ambiguity rather than silently
          // pick the contact. Mesh peers handled the same way.
          const collidesWithLocalAgent = !!this.registry.getAgent(name)
          const collidesWithMeshAgent = this.mesh?.directory()?.some((p) => p.skills.some((s) => s.id.toLowerCase() === name.toLowerCase()))
          if (collidesWithLocalAgent || collidesWithMeshAgent) {
            this.json(res, 409, {
              error: "ambiguous",
              hint: `"${name}" resolves to BOTH a contact (id=${result.contact.id}) and an agent. Use /send/agent for the agent, or pass contactId="${result.contact.id}" to /send/contact for the contact.`,
            })
            break
          }
          const picked = this.contacts.pickChannel(result.contact, preferredChannel)
          if (!picked) {
            this.json(res, 400, { error: `Contact "${result.contact.id}" has no channels configured` })
            break
          }
          if (preferredChannel && picked.channel !== preferredChannel) {
            this.json(res, 400, { error: `Contact "${result.contact.id}" has no "${preferredChannel}" channel`, available: Object.keys(result.contact.channels) })
            break
          }
          try {
            const messageId = await this.router.sendOutbound({
              channel: picked.channel,
              chatId: picked.address,
              text,
              agentId: body.agentId as string | undefined,
              accountId: body.accountId as string | undefined,
            })
            this.json(res, 200, { ok: true, messageId: messageId || null, contactId: result.contact.id, channel: picked.channel })
          } catch (e: any) {
            this.json(res, 400, { error: e.message })
          }
          break
        }

        case "POST /send": {
          const body = await readBody(req)
          if (!body.channel || !body.chatId || !body.text) {
            this.json(res, 400, { error: "Required: channel, chatId, text" })
            return
          }
          try {
            const messageId = await this.router.sendOutbound({
              channel: body.channel as string,
              chatId: body.chatId as string,
              text: body.text as string,
              replyTo: body.replyTo as string | undefined,
              parseMode: body.parseMode as any,
              agentId: body.agentId as string | undefined,
              accountId: body.accountId as string | undefined,
              // Optional rich payloads (buttons/poll/media) — enables cron
              // jobs and API callers to send interactive messages directly.
              buttons: body.buttons as any,
              poll: body.poll as any,
              media: body.media as any,
            })
            this.json(res, 200, { ok: true, messageId: messageId || null })
          } catch (e: any) {
            this.json(res, 400, { error: e.message })
          }
          break
        }

        case "GET /channels":
          this.json(res, 200, this.router.getChannelNames())
          break

        case "GET /services":
          this.json(res, 200, (this.router as any).serviceMatcher?.list() || [])
          break

        case "POST /task": {
          const body = await readBody(req)
          const agentId = (body.agent as string) || this.config.node.defaultAgent
          if (!agentId || !body.message) {
            this.json(res, 400, { error: "Missing: message (and no defaultAgent configured)" })
            return
          }
          // A2A sender identity — when present, must resolve to an agent on
          // the calling peer. Log-warn (not enforce) for one release so
          // older mesh peers can roll out the field before we reject. The
          // shape "no body.context" is what classic A2A calls look like;
          // human-facing /task callers (CLI, dashboard) always set
          // context.channel, so the warning targets only mesh traffic.
          const senderAgentId = typeof body.senderAgentId === "string" ? body.senderAgentId : undefined
          const looksLikeA2A = !body.context
          if (looksLikeA2A && !senderAgentId) {
            this.log(`[a2a] /task accepted without senderAgentId for agent="${agentId}" from ${(req.socket?.remoteAddress) || "unknown"} — caller should upgrade. Required in next release.`)
          }
          // Per-task context strategy override. When absent, registry falls
          // back to config.session.contextStrategy. Used by the bench
          // harness to A/B the same request under "layered" vs "planner"
          // without a daemon reload.
          const contextStrategy =
            body.contextStrategy === "layered" || body.contextStrategy === "planner"
              ? body.contextStrategy
              : undefined
          // Phase 1 commit 6.d — record the inbound mesh dispatch decision
          // in the ledger. The mesh protocol has no stable request id, so
          // each call records as its own event row (no per-event idempotency).
          // Wrapped in try/catch so a ledger failure cannot break /task —
          // legacy stays authoritative until 1c per-source promotion lands.
          let intentRef: { eventId: string; decidedBy: string } | undefined
          if (getLedgerMode("mesh") !== "off") {
            try {
              const decision = recordMeshDispatch(
                getDefaultLedger(),
                {
                  agentId,
                  senderAgentId,
                  context: body.context as any,
                },
                JSON.stringify({ agentId, senderAgentId, context: body.context, message: typeof body.message === "string" ? body.message.slice(0, 200) : "" }),
                {
                  agentId,
                  outcome: "dispatched",
                  reason: senderAgentId ? `from ${senderAgentId}` : null,
                },
              )
              if (decision.outcome === "dispatched") {
                intentRef = { eventId: decision.eventId, decidedBy: decision.decidedBy }
              }
            } catch (e: any) {
              this.log(`[ledger] mesh /task agent="${agentId}" record failed: ${e?.message ?? e}`)
            }
          }

          // No-op onDelta enables stream-json runtime mode so the dashboard
          // task modal can see tool calls + tool results live. The caller still
          // awaits the final response — they don't see deltas, just the result.
          //
          // body.freshSession (improvement plan #8): when true, the dispatcher
          // clears the cached claudeSessionId AND kills any persistent-process
          // handle for this (agent, channel, chatId) before executing — used
          // by triage→sub-agent delegation paths to defend against warm-pool
          // stickiness across visitors.
          //
          // Auto-default for A2A traffic: when senderAgentId is set (an agent
          // calling another agent — typically a triage/router delegating to a
          // worker), default freshSession to true so the worker doesn't inherit
          // the previous visitor's conversation memory. Callers that DO want
          // sticky cross-agent state can opt out with `freshSession: false`.
          // Direct human-facing calls (CLI, dashboard, channels) keep the
          // default-false behavior so chat threads remain warm across turns.
          const explicitFresh = typeof body.freshSession === "boolean" ? body.freshSession : undefined
          const freshSession = explicitFresh !== undefined
            ? explicitFresh
            : (senderAgentId ? true : undefined)

          // Streaming mode — caller can opt in with `Accept: text/event-stream`
          // or `body.stream:true`. Emits a simple agentx-shaped SSE wire
          // (`event: <kind>\ndata: <json>\n\n`) so peers, the Laravel
          // jort-wiki proxy, and any browser/EventSource client get
          // incremental visibility into the orchestrator's progress
          // instead of a 60-180s blank wait. Existing JSON callers are
          // untouched — they see the original single-response shape.
          const acceptHeader = String(req.headers["accept"] || "")
          const wantStream = body.stream === true || acceptHeader.includes("text/event-stream")
          if (wantStream) {
            res.writeHead(200, {
              "Content-Type": "text/event-stream; charset=utf-8",
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Connection: "keep-alive",
              "X-Accel-Buffering": "no",
              "Access-Control-Allow-Origin": "*",
            })
            const writeSse = (event: string, payload: unknown) => {
              try { res.write(`event: ${event}\ndata: ${JSON.stringify(payload)}\n\n`) } catch { /* socket gone */ }
            }
            writeSse("start", { agentId, startedAt: Date.now() })
            const heartbeat = setInterval(() => {
              try { res.write(": ping\n\n") } catch { /* */ }
            }, 15_000)
            ;(heartbeat as any).unref?.()
            // Client-disconnect → interrupt (the chat REPL's "esc to
            // interrupt"). Best-effort: kills a persistent claude process for
            // this (agent, channel, chatId) so it stops mid-turn; a no-op for
            // spawn-per-task agents (whose output is simply discarded once the
            // stream closes). Guarded so it never fires after normal
            // completion.
            let streamDone = false
            req.on("close", () => {
              if (streamDone) return
              streamDone = true
              clearInterval(heartbeat)
              const ctx = (body.context ?? {}) as { channel?: string; chatId?: string }
              if (ctx.channel && ctx.chatId) {
                void this.processRegistry?.kill({ agentId, channel: ctx.channel, chatId: ctx.chatId }, "client-interrupt").catch(() => {})
              }
            })
            const onDelta = (text: string) => { if (text) writeSse("text", { text }) }
            const onThinking = (text: string) => { if (text) writeSse("thinking", { text }) }
            // Map orchestrator stream events → public SSE shape. The
            // orchestrator (`src/agent/index.ts`) emits flat
            // `{type:"tool_call", name, id}` and `{type:"tool_result",
            // name, id, is_error}` events directly; we only forward
            // those plus the claude-code stream-json equivalents so
            // either tier drives the ToolBadge UI without leaking
            // provider internals.
            // Short, human argument preview for a tool badge (● Read(app.ts)).
            // Prefers the identifying field per tool, basenames file paths,
            // and clips. Empty string when nothing useful is available.
            const toolArg = (input: any): string => {
              if (!input || typeof input !== "object") return ""
              const path = input.file_path ?? input.path ?? input.notebook_path
              if (typeof path === "string") return path.split("/").pop() || path
              const v = input.command ?? input.pattern ?? input.query ?? input.url ?? input.prompt ?? input.description
              if (typeof v === "string") return v.replace(/\s+/g, " ").slice(0, 48)
              const first = Object.values(input).find((x) => typeof x === "string") as string | undefined
              return first ? first.replace(/\s+/g, " ").slice(0, 48) : ""
            }
            const emittedTools = new Set<string>()
            const onEvent = (event: any) => {
              const kind = event?.type
              if (kind === "tool_call") {
                writeSse("tool", { status: "start", id: event.id, name: event.name, arg: toolArg(event.input) })
              } else if (kind === "tool_result") {
                writeSse("tool", {
                  status: "result",
                  id: event.id,
                  name: event.name,
                  error: event.is_error === true,
                })
              } else if (kind === "assistant" && Array.isArray(event.message?.content)) {
                // claude-code tier: the assistant message carries complete
                // tool_use blocks (name + full input) — unlike
                // content_block_start, whose input is still empty. Dedup by
                // block id so re-emitted message snapshots don't double-badge.
                for (const block of event.message.content) {
                  if (block?.type === "tool_use" && block.id && !emittedTools.has(block.id)) {
                    emittedTools.add(block.id)
                    writeSse("tool", { status: "start", id: block.id, name: block.name, arg: toolArg(block.input) })
                  }
                }
              }
            }
            try {
              const resp = await this.registry.execute(
                {
                  agentId,
                  message: body.message as string,
                  context: body.context as any,
                  contextStrategy,
                  intentRef,
                  freshSession,
                },
                onDelta,
                onThinking,
                onEvent,
              )
              streamDone = true
              clearInterval(heartbeat)
              if (resp.error) {
                writeSse("error", { error: resp.error, errorKind: resp.errorKind })
              } else {
                writeSse("done", {
                  content: resp.content,
                  duration: resp.duration,
                  usage: resp.usage,
                })
              }
            } catch (e: any) {
              streamDone = true
              clearInterval(heartbeat)
              writeSse("error", { error: e?.message ?? String(e) })
            }
            try { res.end() } catch { /* */ }
            break
          }

          const response = await this.registry.execute(
            {
              agentId,
              message: body.message as string,
              context: body.context as any,
              contextStrategy,
              intentRef,
              freshSession,
            },
            () => {},
          )
          this.json(res, response.error ? 500 : 200, response)
          break
        }

        case "POST /ask":
        case "GET /ask": {
          // Voice-optimized endpoint for Siri/voice assistants
          // Accepts message via body.message (POST) or ?q= (GET)
          const url = new URL(req.url || "/", `http://${req.headers.host || "localhost"}`)
          let message = url.searchParams.get("q") || url.searchParams.get("message") || ""
          if (req.method === "POST") {
            const body = await readBody(req)
            message = (body.message as string) || (body.q as string) || message
          }

          const agentId = this.config.node.defaultAgent
          if (!agentId) {
            this.json(res, 400, { error: "No defaultAgent configured in node config" })
            return
          }
          if (!message) {
            this.json(res, 400, { error: "Missing message (pass as ?q=... or {message:...})" })
            return
          }

          // Prepend instruction so the agent responds in a TTS-friendly way
          const voicePrompt = `[VOICE MODE — Your response will be spoken aloud by a TTS engine. Keep it to 2-3 short sentences. Use plain language, no markdown, no code blocks, no bullet points, no URLs. Speak conversationally.]\n\n${message}`

          const response = await this.registry.execute({
            agentId,
            message: voicePrompt,
            context: { channel: "voice", sender: "Siri" },
          })

          // Convert response to speakable text (TTS-friendly)
          const speakable = toSpeakable(response.content)

          this.json(res, response.error ? 500 : 200, {
            text: speakable,
            full: response.content,
            error: response.error,
            duration: response.duration,
          })
          break
        }

        case "POST /mesh/task": {
          const body = await readBody(req)
          if (!body.peer || !body.message) {
            this.json(res, 400, { error: "Missing: peer, message" })
            return
          }
          if (!this.mesh) {
            this.json(res, 400, { error: "Mesh not enabled" })
            return
          }

          // Streaming pass-through: forward the peer's `/task` SSE to the
          // caller verbatim. Used by the jort-wiki Laravel proxy so the
          // browser sees the orchestrator's text/thinking/tool events
          // arrive incrementally instead of waiting for the whole answer.
          const acceptHeader = String(req.headers["accept"] || "")
          const wantStream = body.stream === true || acceptHeader.includes("text/event-stream")
          if (wantStream) {
            res.writeHead(200, {
              "Content-Type": "text/event-stream; charset=utf-8",
              "Cache-Control": "no-cache, no-store, must-revalidate",
              Connection: "keep-alive",
              "X-Accel-Buffering": "no",
              "Access-Control-Allow-Origin": "*",
            })
            const writeSse = (event: string, payload: unknown) => {
              try { res.write(`event: ${event}\ndata: ${JSON.stringify(payload)}\n\n`) } catch { /* */ }
            }
            const heartbeat = setInterval(() => {
              try { res.write(": ping\n\n") } catch { /* */ }
            }, 15_000)
            ;(heartbeat as any).unref?.()
            try {
              for await (const ev of this.mesh.sendTaskStream(
                body.peer as string,
                body.message as string,
                body.agent as string | undefined,
                { context: body.context as any },
              )) {
                writeSse(ev.event, ev.data)
              }
            } catch (e: any) {
              writeSse("error", { error: e?.message ?? String(e) })
            } finally {
              clearInterval(heartbeat)
              try { res.end() } catch { /* */ }
            }
            break
          }

          const result = await this.mesh.sendTask(body.peer as string, body.message as string, body.agent as string | undefined)
          this.json(res, 200, { response: result })
          break
        }

        // Browser → daemon → remote peer
        case "POST /webrtc/signal/out": {
          if (!this.webrtc) {
            this.json(res, 404, { error: "WebRTC signaling not enabled" })
            return
          }
          const body = await readBody(req)
          const signal = body as unknown as WebRtcSignal
          if (!signal.kind || !signal.callId || !signal.from || !signal.to) {
            this.json(res, 400, { error: "Missing: kind, callId, from, to" })
            return
          }
          const result = await this.webrtc.handleOutgoing(signal)
          this.json(res, result.ok ? 200 : 400, result)
          break
        }

        // Remote peer → daemon → local browser (SSE fan-out).
        // Called by `A2AMesh.sendSignal()` on the sending peer.
        case "POST /webrtc/signal": {
          if (!this.webrtc) {
            this.json(res, 404, { error: "WebRTC signaling not enabled" })
            return
          }
          const body = await readBody(req)
          const signal = body as unknown as WebRtcSignal
          if (!signal.kind || !signal.callId || !signal.from || !signal.to) {
            this.json(res, 400, { error: "Missing: kind, callId, from, to" })
            return
          }
          const result = this.webrtc.handleIncoming(signal)
          this.json(res, result.ok ? 200 : 400, result)
          break
        }

        // Browser asks the local daemon to spawn a bot peer for this call.
        case "POST /webrtc/bot/invite": {
          if (!this.botManager) {
            this.json(res, 404, { error: "WebRTC bot not enabled (channels.webrtc.bot.enabled=false)" })
            return
          }
          const body = await readBody(req)
          const callId = body.callId as string | undefined
          const target = body.target as string | undefined
          const agentId = (body.agentId as string | undefined) || this.config.channels.webrtc?.bot.defaultAgentId
          if (!callId || !target || !agentId) {
            this.json(res, 400, { error: "Missing: callId, target, agentId (or defaultAgentId in config)" })
            return
          }
          const result = await this.botManager.invite({ callId, target, agentId })
          this.json(res, result.ok ? 200 : 500, result)
          break
        }

        case "GET /webrtc/bots": {
          if (!this.botManager) {
            this.json(res, 404, { error: "WebRTC bot not enabled" })
            return
          }
          this.json(res, 200, { active: this.botManager.active() })
          break
        }

        case "GET /webrtc/history": {
          if (!this.botManager) {
            this.json(res, 404, { error: "WebRTC bot not enabled" })
            return
          }
          // In-process ring buffer of recently-completed sessions. Surfaces
          // call activity to the admin UI without requiring transcript
          // persistence to disk (that's still opt-in via webrtcBot.transcriptChannel).
          this.json(res, 200, { active: this.botManager.active(), history: this.botManager.history() })
          break
        }

        case "GET /webrtc/config": {
          const wrtc = this.config.channels.webrtc
          if (!wrtc?.enabled) {
            this.json(res, 404, { error: "WebRTC signaling not enabled" })
            return
          }
          this.json(res, 200, {
            localName: this.config.node.name,
            iceServers: [
              ...wrtc.stunServers.map(urls => ({ urls })),
              ...wrtc.turnServers,
            ],
            peers: this.mesh?.directory().map(p => ({ name: p.peer, healthy: p.healthy })) || [],
          })
          break
        }

        // A2A agent card discovery
        case "GET /.well-known/agent-card.json":
          this.json(res, 200, {
            name: this.config.node.name,
            description: `AgentX daemon node "${this.config.node.name}"`,
            url: `http://${this.config.node.bind}`,
            version: "1.0.0",
            capabilities: {
              streaming: false,
              pushNotifications: false,
              stateTransitionHistory: false,
            },
            // Each agent advertised as a skill (legacy A2A naming). The
            // description is what peers SEE in their [Landscape]: anaemic
            // text here means the calling agent has no idea what we do.
            // Prefer (in order) an explicit `description`, the first
            // sentence of the systemPrompt, then the agent name as a
            // last-resort placeholder.
            skills: this.registry.list().map((a) => {
              // registry.list() returns a flat shape; pull the raw def
              // from the config so we can read systemPrompt / mentions
              // / description.
              const def = (this.config.agents as any)[a.id] || {}
              const desc =
                (typeof def.description === "string" && def.description.trim()) ||
                firstSentence(def.systemPrompt) ||
                `Agent "${a.name}" (${a.tier})`
              const mentions = Array.isArray(def.mentions) ? def.mentions.slice(0, 4) : []
              return {
                id: a.id,
                name: a.name,
                description: desc,
                tags: [a.tier, ...mentions],
              }
            }),
            // Channels this node hosts. Used by mesh peers to route
            // workflow `action.send` calls back to the originating channel
            // when the workflow runs on a different node than the channel
            // adapter (e.g. workflow on macbook, whatsapp on clawd-server).
            channels: this.router.getChannelNames(),
            defaultInputModes: ["text"],
            defaultOutputModes: ["text"],
          })
          break

        // --- Wiki API (for mesh sync) ---

        case "GET /wiki/agents": {
          const hub = this.registry.getWikiHub()
          this.json(res, 200, {
            nodeId: this.config.node.id,
            agents: hub.summary(),
          })
          break
        }

        case "GET /wiki/entries": {
          const hub = this.registry.getWikiHub()
          const agentId = url.searchParams.get("agent") || undefined
          const after = url.searchParams.get("after") || undefined
          const entries = agentId
            ? hub.getAgentEntries(agentId)
            : hub.getSharedStore().listEntries({ after })
          this.json(res, 200, {
            nodeId: this.config.node.id,
            count: entries.length,
            entries: entries.map(e => ({
              id: e.id,
              date: e.date,
              agentId: e.agentId,
              source: e.source,
              sourceContext: e.sourceContext,
              content: e.content,
            })),
          })
          break
        }

        case "GET /wiki/articles": {
          const hub = this.registry.getWikiHub()
          const agentId = url.searchParams.get("agent")
          if (!agentId) {
            this.json(res, 400, { error: "?agent= required" })
            break
          }
          const store = hub.getAgentWiki(agentId)
          const index = store.rebuildIndex()
          this.json(res, 200, {
            nodeId: this.config.node.id,
            agentId,
            articles: index.articles,
          })
          break
        }

        case "GET /wiki/article": {
          const hub = this.registry.getWikiHub()
          const agentId = url.searchParams.get("agent")
          const articlePath = url.searchParams.get("path")
          if (!agentId || !articlePath) {
            this.json(res, 400, { error: "?agent= and ?path= required" })
            break
          }
          const store = hub.getAgentWiki(agentId)
          const article = store.readArticle(articlePath)
          if (!article) {
            this.json(res, 404, { error: "Article not found" })
            break
          }
          this.json(res, 200, {
            nodeId: this.config.node.id,
            agentId,
            path: article.path,
            title: article.meta.title,
            tags: article.meta.tags,
            owner: article.meta.owner,
            created: article.meta.created,
            lastUpdated: article.meta.lastUpdated,
            sources: article.meta.sources,
            content: article.content,
          })
          break
        }

        // --- Graph API (read-only, for mesh sync) ---

        case "GET /graph/schema": {
          const store = this.registry.getGraphStore()
          if (!store) { this.json(res, 404, { error: "graph disabled on this node" }); break }
          this.json(res, 200, {
            nodeId: this.config.node.id,
            schema: store.loadSchema(),
          })
          break
        }

        case "GET /graph/nodes": {
          const store = this.registry.getGraphStore()
          if (!store) { this.json(res, 404, { error: "graph disabled on this node" }); break }
          this.json(res, 200, {
            nodeId: this.config.node.id,
            ...store.loadNodes(),
          })
          break
        }

        case "GET /graph/classifications": {
          const store = this.registry.getGraphStore()
          if (!store) { this.json(res, 404, { error: "graph disabled on this node" }); break }
          const status = (url.searchParams.get("status") || "approved") as "pending" | "approved" | "rejected"
          const limit = Math.max(1, Math.min(1000, parseInt(url.searchParams.get("limit") || "200", 10) || 200))
          const items = store.listByStatus(status, limit)
          this.json(res, 200, {
            nodeId: this.config.node.id,
            status,
            count: items.length,
            classifications: items,
          })
          break
        }

        default:
          this.json(res, 404, {
            error: "Not found",
            endpoints: [
              "GET  /health",
              "GET  /agents",
              "GET  /crons",
              "GET  /mesh",
              "GET  /wiki/agents",
              "GET  /wiki/entries[?agent=X&after=YYYY-MM-DD]",
              "GET  /wiki/articles?agent=X",
              "GET  /graph/schema",
              "GET  /graph/nodes",
              "GET  /graph/classifications[?status=approved|pending|rejected&limit=N]",
              "POST /task { agent, message, context?, freshSession?: bool }",
              "GET  /agents/:id  — resolved agent config (permission, tier, model, persistentProcess, toolUseRequired)",
              "POST /agents/:id/selftest { message? }  — canary probe; runs a fresh-session task and reports {ok, durationMs, tokens, billedModel}",
              "GET  /traces[?agentId=&channel=&chatId=&workflowRunId=&status=&since=&until=&limit=]",
              "GET  /traces/:taskId  — full per-task execution trace (steps + tokens)",
              "GET  /api/processes  — live persistent claude processes (JSON)",
              "POST /api/processes/kill { agentId, channel, chatId, reason? }",
              "GET  /api/actions/builtin  — list shipped built-in typed actions",
              "POST /api/actions/builtin/:name  — run a built-in with JSON body input",
              "POST /mesh/task { peer, message }",
              "POST /webhook/:agentId[/:source]  — webhook callback",
              "POST /reload  — re-read agentx.json (hot-swaps crons)",
              "GET  /.well-known/agent-card.json",
              "GET  /call  — browser UI for P2P A/V calls (requires channels.webrtc.enabled)",
              "GET  /webrtc/config  — ICE servers + peer directory for the call page",
              "GET  /webrtc/events?callId=&as=  — SSE stream of signaling events",
              "POST /webrtc/signal/out  — browser-originated signal, forwarded to remote peer",
              "POST /webrtc/signal  — remote-peer-originated signal, fanned out to local browser",
              "POST /webrtc/bot/invite { callId, target, agentId }  — spawn a transcribing bot peer for a call",
              "GET  /webrtc/bots  — active bot sessions",
            ],
          })
      }
    } catch (e: any) {
      this.json(res, 500, { error: e.message })
    }
  }

  /**
   * OpenAI-compatible chat completions endpoint.
   * Allows ElevenLabs, Cursor, or any OpenAI-compatible client to use an AgentX agent as an LLM.
   *
   * Routes:
   *   POST /v1/chat/completions                    — uses "model" field as agent ID
   *   POST /llm/:agentId/v1/chat/completions       — explicit agent ID in URL
   *
   * Request format (OpenAI): { model, messages: [{role, content}], stream?, temperature? }
   * Response format (OpenAI): { id, object, choices: [{message: {role, content}}], usage }
   */
  private async handleOpenAICompat(req: IncomingMessage, res: ServerResponse, path: string): Promise<void> {
    const body = await readBody(req)

    // Resolve agent ID: from URL path or "model" field
    const pathMatch = path.match(/^\/llm\/([^/]+)\//)
    const fallbackAgent = Object.keys(this.config.agents)[0] || "default"
    const agentId = pathMatch?.[1] || (body.model as string) || fallbackAgent

    // Extract messages — use the last user message as the task
    const messages = (body.messages as Array<{ role: string; content: string }>) || []
    const lastUserMsg = [...messages].reverse().find(m => m.role === "user")

    if (!lastUserMsg?.content) {
      this.json(res, 400, { error: { message: "No user message found", type: "invalid_request_error" } })
      return
    }

    // Build conversation context from message history
    const historyLines = messages
      .slice(0, -1) // exclude the last message (it's the prompt)
      .map(m => `${m.role === "user" ? "User" : "Assistant"}: ${m.content.slice(0, 200)}`)

    const contextPrefix = historyLines.length > 0
      ? `[Conversation]\n${historyLines.slice(-10).join("\n")}\n\n`
      : ""

    const stream = body.stream === true

    if (stream) {
      // SSE streaming response
      res.writeHead(200, {
        "Content-Type": "text/event-stream",
        "Cache-Control": "no-cache",
        "Connection": "keep-alive",
      })

      const requestId = `chatcmpl-${Date.now().toString(36)}`

      const response = await this.registry.execute({
        agentId,
        message: contextPrefix + lastUserMsg.content,
        context: { channel: "api", sender: "openai-compat" },
      })

      const content = response.error || response.content || ""

      // Send as a single chunk (Claude Code doesn't stream to us via execFile)
      const chunk = {
        id: requestId,
        object: "chat.completion.chunk",
        created: Math.floor(Date.now() / 1000),
        model: agentId,
        choices: [{
          index: 0,
          delta: { role: "assistant", content },
          finish_reason: "stop",
        }],
      }
      res.write(`data: ${JSON.stringify(chunk)}\n\n`)
      res.write("data: [DONE]\n\n")
      res.end()
    } else {
      // Standard response
      const response = await this.registry.execute({
        agentId,
        message: contextPrefix + lastUserMsg.content,
        context: { channel: "api", sender: "openai-compat" },
      })

      const content = response.error || response.content || ""
      const tokens = Math.ceil(content.length / 4)

      this.json(res, 200, {
        id: `chatcmpl-${Date.now().toString(36)}`,
        object: "chat.completion",
        created: Math.floor(Date.now() / 1000),
        model: agentId,
        choices: [{
          index: 0,
          message: { role: "assistant", content },
          finish_reason: "stop",
        }],
        usage: {
          prompt_tokens: Math.ceil(lastUserMsg.content.length / 4),
          completion_tokens: tokens,
          total_tokens: Math.ceil(lastUserMsg.content.length / 4) + tokens,
        },
      })
    }
  }

  private json(res: ServerResponse, status: number, data: unknown): void {
    res.writeHead(status, { "Content-Type": "application/json" })
    res.end(JSON.stringify(data, null, 2))
  }

  /** Resolve a GitLab API token for the given agent, checking (in order):
   *    1. `channels.gitlab.agentMappings[].token` — legacy per-agent token
   *    2. `agents[id].integrations[]` (kind=gitlab-user, tokenEnv) — newer
   *       style used by agents declared via `agentx manage`
   *    3. `channels.gitlab.token` — global fallback (group bot identity)
   *  Returns the token plus a `source` tag for diagnostic logging. */
  private resolveGitlabTokenForAgent(agentId?: string): { token: string | undefined; source: string } {
    if (!agentId) {
      return { token: this.config.channels.gitlab?.token, source: "global" }
    }
    const mapping = (this.config.channels.gitlab?.agentMappings || []).find((m: any) => m.agentId === agentId)
    if (mapping?.token) return { token: mapping.token, source: "agentMappings" }
    const credential = resolveAgentCredential(this.config, agentId, "gitlab-user", "tokenEnv")
    if (credential.status === "ok" && credential.value) {
      return { token: credential.value, source: `integrations(${credential.envVar})` }
    }
    if (this.config.channels.gitlab?.token) return { token: this.config.channels.gitlab.token, source: "global" }
    return { token: undefined, source: credential.status }
  }

  /** Pick today's usage rollup according to AGENTX_USAGE_READ:
   *    - "sqlite": SQLite only (empty if db null / no rows)
   *    - "json":   JSON only (legacy behaviour)
   *    - "sqlite-then-json" (default): try SQLite, fall back to JSON when
   *      the rollup is empty (no agents seen today). Transparent for
   *      operators on first deploy where SQLite is empty until the first
   *      task completes. */
  private resolveTodayUsage(): ReturnType<AgentRegistry["getTodayUsage"]> {
    const mode = getUsageReadMode()
    if (mode === "json") return this.registry.getTodayUsage()
    const rollup = loadTodayRollup(this.db)
    if (mode === "sqlite") return rollup
    // sqlite-then-json
    if (Object.keys(rollup.agents).length > 0) return rollup
    return this.registry.getTodayUsage()
  }

  /** Boot-time: install the `remember` skill into every agent workspace
   *  that doesn't already have it, and sync each agent's existing memory
   *  into <workspace>/CLAUDE.md + .agentx-memory.md. Idempotent — safe to
   *  run on every daemon start. Write-if-absent for the skill, sentinel-
   *  replace for CLAUDE.md, so operator edits survive. */
  private installAgentMemorySurface(): void {
    for (const agent of this.registry.list()) {
      const ws = agent.workspace
      if (!ws || !existsSync(ws)) continue
      try {
        const skillsDir = resolve(ws, ".claude", "skills")
        mkdirSync(skillsDir, { recursive: true })
        const skillPath = resolve(skillsDir, REMEMBER_SKILL_FILENAME)
        if (!existsSync(skillPath)) {
          writeFileSync(skillPath, REMEMBER_SKILL_BODY)
          this.log(`  memory-skill: installed remember.md → ${agent.id}`)
        }
        // Always re-sync: rewrites .agentx-memory.md and the CLAUDE.md
        // sentinel block from whatever is currently on disk.
        this.agentMemory.syncToWorkspace(agent.id, ws)
      } catch (e: any) {
        this.log(`  memory-skill: ${agent.id} install failed — ${e?.message ?? e}`)
      }
    }
  }

  /** Boot-time: sync each agent's `mcp` config block to
   *  <workspace>/.mcp.json. Mirrors installAgentMemorySurface — same
   *  ownership semantics, marker-based to preserve operator edits. */
  private installAgentMcpConfig(): void {
    for (const [agentId, def] of Object.entries(this.config.agents)) {
      const ws = def.workspace
      if (!ws || !existsSync(ws)) continue
      // effectiveMcpConfig layers the codegraph server on top of any
      // operator-declared MCP servers when `def.codegraph === true`.
      // Operator entries still win on collision (see effectiveMcpConfig).
      const mcp = effectiveMcpConfig(def)
      try {
        const result = syncMcpToWorkspace(ws, mcp)
        switch (result) {
          case "installed":
            this.log(`  mcp: installed ${Object.keys(mcp).length} server(s) → ${agentId}`)
            break
          case "updated":
            this.log(`  mcp: updated .mcp.json (${Object.keys(mcp).length} server(s)) → ${agentId}`)
            break
          case "removed":
            this.log(`  mcp: removed managed .mcp.json (config now empty) → ${agentId}`)
            break
          case "skipped-operator-owned":
            // Only worth surfacing when there's a config the operator
            // is silently overriding. Otherwise stay quiet.
            if (Object.keys(mcp).length > 0) {
              this.log(`  mcp: ${agentId} has operator-owned .mcp.json — skipping (delete file or marker to let agentx manage)`)
            }
            break
          case "noop":
            break
        }
      } catch (e: any) {
        this.log(`  mcp: ${agentId} install failed — ${e?.message ?? e}`)
      }
    }
  }

  /** Look up an agent's workspace from the live registry. Returns null
   *  for agents the daemon doesn't know about (e.g., a stale memory
   *  entry for a since-removed agent). */
  private workspaceFor(agentId: string): string | null {
    for (const a of this.registry.list()) if (a.id === agentId) return a.workspace
    return null
  }

  /** Agent-memory HTTP surface. Agents call this from inside a Claude
   *  Code session via `Bash` + `curl` — see the `remember` skill. The
   *  heavy lifting is in `AgentRegistry.agentMemory` (AgentMemory); this
   *  is a thin JSON wrapper that validates + delegates. */
  private async handleMemoryApi(
    req: IncomingMessage, res: ServerResponse, path: string, url: URL,
  ): Promise<boolean> {
    const mem = this.agentMemory

    // GET /api/memory?agent=<id>
    if (req.method === "GET" && path === "/api/memory") {
      const agent = url.searchParams.get("agent") || ""
      if (!agent) { this.json(res, 400, { error: "missing agent query param" }); return true }
      this.json(res, 200, { agent, memories: mem.list(agent), index: mem.indexMarkdown(agent) })
      return true
    }
    // GET /api/memory/:name?agent=<id>
    const oneMatch = req.method === "GET" && path.match(/^\/api\/memory\/([^\/?]+)$/)
    if (oneMatch) {
      const agent = url.searchParams.get("agent") || ""
      if (!agent) { this.json(res, 400, { error: "missing agent query param" }); return true }
      const rec = mem.get(agent, decodeURIComponent(oneMatch[1]))
      if (!rec) { this.json(res, 404, { error: "no such memory" }); return true }
      this.json(res, 200, { memory: rec })
      return true
    }
    // POST /api/memory — write a memory
    if (req.method === "POST" && path === "/api/memory") {
      let body: any
      try { body = await readJsonBody(req) } catch (e: any) {
        this.json(res, 400, { error: "invalid JSON body", message: e.message }); return true
      }
      const agentId = typeof body?.agentId === "string" ? body.agentId : ""
      const type    = typeof body?.type === "string" ? body.type : ""
      const name    = typeof body?.name === "string" ? body.name : ""
      const description = typeof body?.description === "string" ? body.description : ""
      const newBody = typeof body?.body === "string" ? body.body : ""
      const append  = body?.append === true
      if (!agentId || !type || !name || !description || !newBody) {
        this.json(res, 400, { error: "required fields: agentId, type, name, description, body" })
        return true
      }
      try {
        let finalBody = newBody
        if (append) {
          const existing = mem.get(agentId, name)
          if (existing) finalBody = `${existing.body.trimEnd()}\n\n${newBody.trim()}`
        }
        const rec = mem.save({ agentId, type: type as any, name, description, body: finalBody })
        const ws = this.workspaceFor(agentId)
        if (ws) { try { mem.syncToWorkspace(agentId, ws) } catch { /* best effort */ } }
        this.json(res, 200, { ok: true, memory: rec, syncedToWorkspace: !!ws })
      } catch (e: any) {
        this.json(res, 400, { error: e?.message || "save failed" })
      }
      return true
    }
    // DELETE /api/memory/:name?agent=<id>
    const delMatch = req.method === "DELETE" && path.match(/^\/api\/memory\/([^\/?]+)$/)
    if (delMatch) {
      const agent = url.searchParams.get("agent") || ""
      if (!agent) { this.json(res, 400, { error: "missing agent query param" }); return true }
      const ok = mem.remove(agent, decodeURIComponent(delMatch[1]))
      if (!ok) { this.json(res, 404, { error: "no such memory" }); return true }
      const ws = this.workspaceFor(agent)
      if (ws) { try { mem.syncToWorkspace(agent, ws) } catch { /* best effort */ } }
      this.json(res, 200, { ok: true })
      return true
    }
    return false
  }

  private async handleWorkflowEvent(req: IncomingMessage, res: ServerResponse): Promise<void> {
    if (!this.workflowDispatcher) {
      this.json(res, 503, { error: "workflow engine not enabled on this node" })
      return
    }
    let body: any
    try { body = await readJsonBody(req) } catch (e: any) {
      this.json(res, 400, { error: "invalid JSON body", message: e.message }); return
    }
    const payload = body?.payload ?? body
    const event = payload?.event
    const entityRef = payload?.entityRef
    if (!event?.id || !entityRef?.backend || !entityRef?.id) {
      this.json(res, 400, { error: "missing event or entityRef" }); return
    }

    // Two flavours arrive on this endpoint:
    //   1. kind="trigger" — a fresh trigger broadcast from a peer that
    //      observed the channel event. Trigger fields are carried in the
    //      payload itself; we dispatch with fromRemote so only opted-in
    //      workflows match.
    //   2. (legacy / transition) — a workflow-targeted forward for an active
    //      run home'd here. Reconstruct the trigger from the local workflow
    //      definition just like before.
    const kind = body?.kind
    try {
      if (kind === "trigger" && payload.trigger) {
        const fromPeer = typeof body?.fromPeer === "string" ? body.fromPeer : "unknown"
        this.log(`[workflows/mesh] received trigger from "${fromPeer}" source="${payload.trigger.source}" chat="${payload.trigger.chat ?? "*"}"`)
        const r = await this.workflowDispatcher.dispatch({
          trigger: payload.trigger,
          entityRef,
          event,
          fromRemote: { peer: fromPeer },
        })
        this.log(`[workflows/mesh] dispatched broadcast: matched=${r.claimed.length} ${r.claimed.length ? `(${r.claimed.map((w) => w.id).join(", ")})` : "[none — no workflow with mesh.allowRemote matched]"}`)
        this.json(res, 202, { ok: true, mode: "trigger-broadcast", matched: r.claimed.map((w) => w.id) })
        return
      }
      const wf = payload.workflowId ? this.workflowStore?.get(payload.workflowId) : null
      const triggerNode = wf?.nodes.find((n) => n.type.startsWith("trigger."))
      const cfg = (triggerNode?.config ?? {}) as {
        source?: string
        filter?: { project?: string; repo?: string; chat?: string; labels?: string[] }
      }
      const trigger = {
        source: String(cfg.source ?? "hook"),
        project: cfg.filter?.project,
        repo: cfg.filter?.repo,
        chat: cfg.filter?.chat,
        labels: cfg.filter?.labels,
      }
      await this.workflowDispatcher.dispatch({ trigger, entityRef, event })
      this.json(res, 202, { ok: true })
    } catch (e: any) {
      this.log(`[workflows] /workflow/event failed: ${e.message}`)
      this.json(res, 500, { error: e.message })
    }
  }

  /** Mesh-callable outbound send. A peer's workflow `action.send` invokes
   *  this when the channel lives on this node (e.g. clawd-server hosts
   *  whatsapp; macbook's workflow forwards here). Just unwraps to the local
   *  router's outbound path so all the same per-account/per-bot resolution
   *  applies. Authentication is currently the mesh token at the network
   *  edge — the endpoint trusts callers that reach it. */
  private async handleChannelSend(req: IncomingMessage, res: ServerResponse): Promise<void> {
    let body: any
    try { body = await readJsonBody(req) } catch (e: any) {
      this.json(res, 400, { error: "invalid JSON body", message: e.message }); return
    }
    const channel = String(body?.channel ?? "")
    const chatId = String(body?.chatId ?? "")
    const text = String(body?.text ?? "")
    if (!channel || !chatId || !text) {
      this.json(res, 400, { error: "channel, chatId, and text are required" }); return
    }
    const adapter = this.router.getChannel(channel)
    if (!adapter) {
      this.json(res, 404, { error: `channel "${channel}" not hosted on this node` }); return
    }
    try {
      const messageId = await this.router.sendOutbound({
        channel,
        chatId,
        text,
        accountId: typeof body.accountId === "string" ? body.accountId : undefined,
        parseMode: typeof body.parseMode === "string" ? body.parseMode : undefined,
        replyTo: typeof body.replyTo === "string" ? body.replyTo : undefined,
      } as any)
      this.json(res, 200, { ok: true, messageId: messageId ?? null })
    } catch (e: any) {
      this.log(`[mesh] /channel/send "${channel}" failed: ${e.message}`)
      this.json(res, 500, { error: e.message })
    }
  }

  /** List chats the given channel adapter has observed. Merges local with
   *  every healthy mesh peer that hosts the channel — a channel's "true"
   *  chat list is spread across whichever nodes are actually paired
   *  (e.g. macbook has the channel enabled but WhatsApp Baileys is unpaired,
   *  while clawd-server is paired; macbook's list is empty but we still want
   *  the author to pick from clawd-server's cache). Dedup by `id`; the first
   *  source with a given id wins.
   *
   *  `sources` in the response tells the editor where the entries came from
   *  so it can show that metadata alongside. */
  private async handleChannelChats(
    res: ServerResponse,
    channel: string,
    opts: { localOnly?: boolean } = {},
  ): Promise<void> {
    const seen = new Set<string>()
    const chats: Array<{ id: string; name?: string; kind: "dm" | "group"; accountId?: string; source: string }> = []
    const sources: string[] = []
    const add = (items: Array<{ id: string; name?: string; kind: "dm" | "group"; accountId?: string }>, source: string) => {
      let added = 0
      for (const c of items) {
        if (!c.id || seen.has(c.id)) continue
        seen.add(c.id)
        chats.push({ ...c, source })
        added++
      }
      if (added > 0) sources.push(`${source}:${added}`)
    }

    // Local adapter — Telegram or WhatsApp shape (duck-typed).
    const local = this.router.getChannel(channel) as unknown as
      | { listKnownChats?: () => Array<{ id: string; name?: string; kind: "dm" | "group"; accountId?: string }>
          listChats?: () => Array<{ jid: string; name: string; isGroup: boolean }> }
      | undefined
    if (local?.listKnownChats) {
      try { add(local.listKnownChats(), "local") } catch { /* adapter still booting */ }
    } else if (local?.listChats) {
      try {
        add(local.listChats().map((c) => ({ id: c.jid, name: c.name, kind: c.isGroup ? ("group" as const) : ("dm" as const) })), "local")
      } catch { /* */ }
    }

    // Mesh peers — fan out in parallel with `?local=1` so peers only report
    // their OWN adapter's chats (no recursive fan-out back to us). Failures
    // don't block the response; a partial result is better than a timeout.
    if (!opts.localOnly && this.mesh) {
      const peers = this.mesh.directory().filter((p) => p.healthy && p.channels?.includes(channel))
      await Promise.allSettled(peers.map(async (peer) => {
        try {
          const r = await fetch(`${peer.peerUrl}/channels/${encodeURIComponent(channel)}/chats?local=1`, {
            headers: this.mesh!.authHeaders(peer.peer),
            signal: AbortSignal.timeout(5000),
          })
          if (!r.ok) return
          const body = await r.json() as { chats?: Array<{ id: string; name?: string; kind?: "dm" | "group"; accountId?: string }> }
          if (Array.isArray(body.chats)) {
            add(body.chats.map((c) => ({ id: c.id, name: c.name, kind: c.kind ?? "dm", accountId: c.accountId })), `mesh:${peer.peer}`)
          }
        } catch {
          // ignore — soft availability; we'll just return what we have
        }
      }))
    }

    this.json(res, 200, { channel, source: sources.join(", ") || "none", chats })
  }

  /** Replay a draft as an ad-hoc workflow. The draft itself stays in
   *  `_drafts/`; we register a copy as `_adhoc-replay-<draftId>-<ts>.yaml`
   *  in the active store, hot-reload, and fire it through the dispatcher.
   *  Input is sourced from a trace pulled out of the draft's
   *  `sourceTaskIds` — caller can override which trace via `taskId` in the
   *  body, or supply a payload directly via `input`. */
  private async handleWorkflowDraftReplay(req: IncomingMessage, res: ServerResponse, draftId: string): Promise<void> {
    if (!this.workflowDispatcher || !this.workflowStore) {
      this.json(res, 503, { error: "workflow engine not enabled on this node" })
      return
    }
    const cfg = this.config.workflows
    if (!cfg?.enabled) {
      this.json(res, 503, { error: "workflows disabled in config" })
      return
    }

    let body: any
    try { body = await readJsonBody(req) } catch { body = {} }

    const draft = getWorkflowDraft(draftId, process.cwd(), { workflowDir: this.workflowStore.baseDir })
    if (!draft) { this.json(res, 404, { error: `draft not found: ${draftId}` }); return }

    // Parse + lint the draft so we don't ship a broken replay workflow into
    // the active store. workflowSchema.parse normalises defaults; the
    // ad-hoc copy gets its own id so promotion stays explicit.
    let draftWorkflow
    try {
      draftWorkflow = workflowSchema.parse(draft.workflow)
    } catch (e: any) {
      this.json(res, 400, { error: `draft is invalid: ${e?.message || e}` }); return
    }

    // Resolve trigger payload. Priority: explicit `input` > taskId-derived >
    // first sourceTaskId-derived. We always include a `taskId` in the
    // payload so downstream nodes can correlate replay vs original.
    let payload: Record<string, unknown> = body?.input && typeof body.input === "object" ? body.input : {}
    let resolvedTaskId: string | undefined = typeof body?.taskId === "string" ? body.taskId : undefined
    if (!resolvedTaskId && Array.isArray(draftWorkflow.sourceTaskIds) && draftWorkflow.sourceTaskIds.length) {
      resolvedTaskId = draftWorkflow.sourceTaskIds[0]
    }
    if (!body?.input && resolvedTaskId && this.db) {
      const trace = getTrace(this.db, resolvedTaskId)
      if (trace) {
        payload = {
          message: trace.task.messagePreview || "",
          taskId: resolvedTaskId,
          originalAgentId: trace.task.agentId,
          originalChannel: trace.task.channel,
          originalChatId: trace.task.chatId,
          replayOf: draftId,
        }
      }
    }

    // The workflow id regex only allows [a-z0-9] as the first char, so the
    // canonical `_adhoc-...` prefix used by the replay-task CLI fails Zod
    // validation when re-parsed. Drop the leading underscore here — the
    // `adhoc-replay-` prefix still keeps adhoc copies easy to spot in
    // listings and disk-grep.
    const replayId = `adhoc-replay-${draftWorkflow.id}-${Date.now().toString(36)}`
    const replayWorkflow = workflowSchema.parse({ ...draftWorkflow, id: replayId, state: "active", status: "review" })
    const dest = resolve(this.workflowStore.baseDir, `${replayId}.yaml`)
    try {
      writeFileSync(dest, renderWorkflowYaml(replayWorkflow))
    } catch (e: any) {
      this.json(res, 500, { error: `failed to write replay workflow: ${e?.message || e}` })
      return
    }
    // WorkflowStore re-reads from disk on every list()/get() call, so the
    // newly-written file is visible to the dispatcher immediately. The
    // existing fs.watch-based reload picks up the same file asynchronously
    // for the cron + trigger registry, but we don't need to wait for it.

    const entityRef = { backend: "manual", id: resolvedTaskId || `replay-${Date.now().toString(36)}` }
    const eventId = `replay:${draftId}:${Date.now()}`

    // Use dispatchWorkflow (not dispatch) so we bypass matchByTrigger.
    // The replay copy has the trigger.manual node from the draft, and
    // matchByTrigger requires a strict cfg.source === t.source match;
    // trigger.manual nodes have no cfg.source, so matchByTrigger always
    // misses. dispatchWorkflow fires the workflow by id directly.
    try {
      const result = await this.workflowDispatcher.dispatchWorkflow({
        workflowId: replayId,
        entityRef,
        event: { id: eventId, payload },
        trigger: { source: "manual" },
      })
      const runId = result.run?.id
      this.json(res, runId ? 200 : 202, {
        ok: true,
        runId,
        replayWorkflowId: replayId,
        sourceTaskId: resolvedTaskId,
      })
    } catch (e: any) {
      this.log(`[workflows] draft replay "${draftId}" failed: ${e.message}`)
      this.json(res, 500, { error: e.message })
    }
  }

  /** Manual run endpoint used by `agentx workflow run <id>`. Only workflows
   *  whose trigger node is `trigger.manual` are runnable here — any other
   *  source expects a live event and shouldn't race with a manual kick. */
  private async handleWorkflowManualRun(req: IncomingMessage, res: ServerResponse, workflowId: string): Promise<void> {
    if (!this.workflowDispatcher || !this.workflowStore) {
      this.json(res, 503, { error: "workflow engine not enabled on this node" })
      return
    }
    const wf = this.workflowStore.get(workflowId)
    if (!wf) { this.json(res, 404, { error: `unknown workflow "${workflowId}"` }); return }
    const triggerNode = wf.nodes.find((n) => n.type.startsWith("trigger."))
    if (!triggerNode) { this.json(res, 400, { error: `workflow "${workflowId}" has no trigger node` }); return }

    let body: any
    try { body = await readJsonBody(req) } catch { body = {} }
    const force = !!body?.force
    const payload = body?.payload || {}

    // By default we only allow running workflows whose trigger is
    // `trigger.manual` — otherwise a manual kick would race against live
    // channel events. `force: true` overrides this for testing: we
    // synthesize a trigger event with the workflow's declared source so
    // the dispatcher's filter still matches, and seed the provided payload
    // into the trigger node's output bundle. Useful when the live channel
    // is disconnected (WhatsApp not paired, Telegram 409 conflict) and
    // you just want to exercise the graph.
    if (triggerNode.type !== "trigger.manual" && !force) {
      this.json(res, 409, {
        error: `workflow "${workflowId}" trigger is "${triggerNode.type}"`,
        hint: `pass { "force": true } to fire anyway with a synthesized event (for testing)`,
      })
      return
    }

    const cfg = (triggerNode.config ?? {}) as {
      source?: string
      filter?: { project?: string; repo?: string; chat?: string; labels?: string[] }
    }
    const source = force ? String(cfg.source ?? "manual") : "manual"
    const entityId = String(payload.entityId || payload.chatId || `manual-${Date.now().toString(36)}`)
    const entityRef = {
      backend: force ? (cfg.source ? "channel" : "manual") : "manual",
      id: entityId,
    }
    const eventId = `manual:${workflowId}:${entityId}:${Date.now()}`
    try {
      const updated = await this.workflowDispatcher.dispatch({
        trigger: force
          ? {
              source,
              project: cfg.filter?.project,
              repo: cfg.filter?.repo,
              chat: cfg.filter?.chat,
              labels: cfg.filter?.labels,
            }
          : { source: "manual" },
        entityRef,
        event: { id: eventId, payload },
      })
      const runId = updated.runs[0]?.id
      this.json(res, runId ? 200 : 202, { ok: true, runId, entityRef, source, force })
    } catch (e: any) {
      this.log(`[workflows] manual run "${workflowId}" failed: ${e.message}`)
      this.json(res, 500, { error: e.message })
    }
  }
}

/** Pull the first sentence (or ~120 chars) out of a systemPrompt so the
 *  agent-card description tells peers something useful. Returns "" when
 *  the input is empty/missing — callers fall through to a default. */
function firstSentence(prompt: unknown): string {
  if (typeof prompt !== "string") return ""
  const trimmed = prompt.trim()
  if (!trimmed) return ""
  const m = trimmed.match(/^[^.!?\n]+[.!?]?/)
  const head = (m ? m[0] : trimmed).trim()
  return head.length > 140 ? head.slice(0, 137) + "…" : head
}

function readJsonBody(req: IncomingMessage): Promise<unknown> {
  return new Promise((resolve, reject) => {
    let raw = ""
    req.setEncoding("utf8")
    req.on("data", (chunk) => { raw += chunk })
    req.on("end", () => {
      try { resolve(raw ? JSON.parse(raw) : {}) } catch (e) { reject(e) }
    })
    req.on("error", reject)
  })
}

/**
 * Convert text to TTS-friendly speech.
 * Strips markdown, expands technical terms, removes code, handles symbols.
 */
function toSpeakable(text: string): string {
  let s = text

  // Remove code blocks entirely (don't read code aloud)
  s = s.replace(/```[\s\S]*?```/g, ". ")
  s = s.replace(/`[^`]*`/g, "") // inline code

  // Remove markdown syntax characters
  s = s.replace(/\*\*(.*?)\*\*/g, "$1")   // bold
  s = s.replace(/__(.*?)__/g, "$1")       // bold
  s = s.replace(/\*([^*]+)\*/g, "$1")     // italic
  s = s.replace(/_([^_]+)_/g, "$1")       // italic
  s = s.replace(/~~(.*?)~~/g, "$1")       // strikethrough

  // Links: keep text, drop URL
  s = s.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")

  // Images: describe
  s = s.replace(/!\[([^\]]*)\]\([^)]+\)/g, "image$1 ")

  // Headers: remove # markers
  s = s.replace(/^#{1,6}\s+/gm, "")

  // List bullets: convert to pauses
  s = s.replace(/^[\s]*[-*+]\s+/gm, ". ")
  s = s.replace(/^[\s]*\d+\.\s+/gm, ". ")

  // Horizontal rules
  s = s.replace(/^[-=_]{3,}$/gm, "")

  // Blockquotes
  s = s.replace(/^>\s*/gm, "")

  // URLs in plain text: replace with "link"
  s = s.replace(/https?:\/\/\S+/g, "link")

  // File paths: keep readable
  s = s.replace(/\/\w+(\/\w+)+/g, (match) => match.split("/").filter(Boolean).join(" slash "))

  // Technical symbols → words
  s = s.replace(/&/g, " and ")
  s = s.replace(/@/g, " at ")
  s = s.replace(/=>/g, " returns ")
  s = s.replace(/->/g, " to ")
  s = s.replace(/\|/g, " or ")

  // Common abbreviations
  s = s.replace(/\be\.g\./gi, "for example")
  s = s.replace(/\bi\.e\./gi, "that is")
  s = s.replace(/\betc\./gi, "etcetera")
  s = s.replace(/\bvs\.?\b/gi, "versus")

  // Collapse whitespace
  s = s.replace(/\n{2,}/g, ". ")
  s = s.replace(/\n/g, " ")
  s = s.replace(/\s+/g, " ")
  s = s.replace(/\.\s*\./g, ".")
  s = s.replace(/\s+([.,!?])/g, "$1")

  // Strip leading/trailing whitespace and punctuation
  s = s.trim().replace(/^[.,:;]+\s*/, "")

  // Cap at 800 chars for voice UX
  if (s.length > 800) {
    s = s.slice(0, 800).replace(/\s+\S*$/, "") + "..."
  }

  return s
}

/**
 * Diff two agent-definition maps and return the list of field names that
 * would require a daemon restart to take effect. Hot-swappable fields
 * (systemPrompt, mentions, maxConcurrent, access, avatar, queueMode,
 * heartbeat, tags) are read fresh per-task so the registry.setConfig swap
 * is enough. Restart-required fields are those captured at Claude Code
 * subprocess spawn time: model, workspace, tier, permissionMode, mcpServers.
 */
function detectAgentRestartFields(
  prev: Record<string, any>,
  next: Record<string, any>,
): string[] {
  const restartFields = ["model", "workspace", "tier", "permissionMode", "mcpServers"]
  const changed = new Set<string>()
  const ids = new Set([...Object.keys(prev), ...Object.keys(next)])
  for (const id of ids) {
    const p = prev[id], n = next[id]
    if (!p || !n) { changed.add("add/remove"); continue }
    for (const f of restartFields) {
      if (JSON.stringify(p[f]) !== JSON.stringify(n[f])) {
        changed.add(`${id}.${f}`)
      }
    }
  }
  return Array.from(changed).slice(0, 6) // cap to keep summary readable
}

async function readBody(req: IncomingMessage): Promise<Record<string, unknown>> {
  return new Promise((resolve, reject) => {
    let body = ""
    req.on("data", (chunk: Buffer) => (body += chunk.toString()))
    req.on("end", () => {
      try {
        resolve(body ? JSON.parse(body) : {})
      } catch {
        resolve({})
      }
    })
    req.on("error", reject)
  })
}
